import type { AuthTokens } from "./auth-tokens";
import { isAccessExpired, isIdleExpired } from "./auth-tokens";
import {
  fetchAiProviders,
  updateAiProviders,
  testAiProvider,
  fetchSavedExtractionStatus,
  retrySavedSection,
  fetchAiProviderList,
  createAiProvider,
  updateAiProviderRow,
  deleteAiProvider,
  testAiProviderById,
  reorderAiProviders,
  type AiProviderConfig,
  type AiProvidersSettingsResponse,
  type AiProviderRow,
  type AiProviderCreatePayload,
  type AiProviderUpdatePayload,
  type SavedExtractionStatusResponse,
  type SavedExtractionSection,
} from "./aiProvidersApi";
import { chaptersAdminApi, chaptersStudentApi } from "./chaptersApi";
import { ingestReviewApi } from "./ingestReviewApi";

export type {
  AiProviderConfig,
  AiProvidersSettingsResponse,
  AiProviderRow,
  AiProviderCreatePayload,
  AiProviderUpdatePayload,
  SavedExtractionStatusResponse,
  SavedExtractionSection,
};

const API_BASE = import.meta.env.VITE_API_URL ?? "http://localhost:8000/api/v1";

export function getBackendOrigin(): string {
  try {
    if (/^https?:\/\//i.test(API_BASE)) {
      return new URL(API_BASE).origin;
    }
    if (typeof window !== "undefined" && window.location?.origin) {
      return new URL(API_BASE, window.location.origin).origin;
    }
  } catch {
    // fallback
  }
  return API_BASE.replace(/\/api\/v1\/?$/i, "").replace(/\/+$/, "");
}

export function resolveImageUrl(url: string | null | undefined): string | null {
  if (!url || typeof url !== "string") return null;
  const trimmed = url.trim();
  if (!trimmed) return null;
  if (/^(?:https?:|\/\/|data:|blob:)/i.test(trimmed)) {
    return trimmed;
  }
  const origin = getBackendOrigin().replace(/\/+$/, "");
  const path = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  return `${origin}${path}`;
}

export type { AuthTokens };
export type UserRole = "student" | "admin";

export type University = {
  id: string;
  name: string;
  short_name: string;
  region?: string | null;
};

export type SessionStatus = {
  phone_verified: boolean;
  password_verified?: boolean;
  otp_sent: boolean;
  is_registered: boolean | null;
  flow?: "login" | "register" | "link_telegram" | "password" | "admin_otp";
};

export type AuthFlow = "login" | "register" | "link_telegram" | "password" | "admin_otp";

export type TelegramLoginUser = {
  id: number;
  first_name?: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
};

export type TelegramLoginResult = {
  telegram_user_id: number;
  telegram_auth_token: string;
  first_name?: string;
  username?: string;
};

export type AdminStat = { label: string; value: string; delta: string };

export type AdminUser = {
  id: string;
  name: string;
  phone: string;
  phoneRaw?: string;
  university: string;
  universityId?: string | null;
  department?: string | null;
  streamId?: string | null;
  streamName?: string | null;
  joined: string;
  plan: "Premium" | "Free";
  status: "Active" | "Suspended";
  avatar: string;
  isPremium?: boolean;
  isActive?: boolean;
  premiumUntil?: string | null;
  preferredLanguage?: string;
  telegramUserId?: number | null;
  quizzes?: number;
  exams?: number;
  cocs?: number;
  activity?: number;
};

export interface ContentFigure {
  id: string;
  caption?: string;
  description?: string;
  page?: number | null;
}

export interface SplitCurriculumTopic {
  id?: string;
  title: string;
  sortOrder?: number;
  startPage?: number | null;
  endPage?: number | null;
}

export interface SplitCurriculumChapter {
  id?: string;
  title: string;
  sortOrder?: number;
  startPage?: number | null;
  endPage?: number | null;
  isDefault?: boolean;
  confidence?: string;
  figures?: ContentFigure[];
  figureCount?: number;
  topics?: SplitCurriculumTopic[];
  content?: {
    id: string;
    version: number;
    status: string;
    source: string;
    wordCount: number;
    pages: number[];
    figures?: ContentFigure[];
    figureCount?: number;
    preview: string;
  } | null;
  exerciseCount?: number;
  exercises?: Array<{ text: string; section?: string }>;
}

export interface CurriculumSplitResponse {
  resourceId: string;
  resourceTitle: string;
  resourceType: string;
  status: "proposed" | "confirmed" | "pending";
  confidence: string;
  chapterCount: number;
  expectedChapterCount?: number | null;
  expectedChapters?: unknown;
  hasMismatch: boolean;
  chapters: SplitCurriculumChapter[];
  totalExercises: number;
}

export interface ConfirmSplitResponse {
  success: boolean;
  resourceId: string;
  status: "confirmed";
  chapterCount: number;
  expectedChapterCount?: number | null;
  hasMismatch: boolean;
  chapters: Array<{
    id: string;
    title: string;
    sortOrder: number;
    startPage?: number | null;
    endPage?: number | null;
    topics: Array<{ id: string; title: string; sortOrder: number }>;
    topicCount: number;
    figures?: ContentFigure[];
    figureCount?: number;
    exerciseCount: number;
    pagesProcessed: number;
    wordCount: number;
    removedNotices: string[];
  }>;
  totalExercises: number;
}

export interface ContentPacketChapter {
  chapterId: string;
  chapterTitle: string;
  sortOrder: number;
  startPage?: number | null;
  endPage?: number | null;
  hasFigures: boolean;
  figureCount: number;
  figureNote?: string;
  figures: ContentFigure[];
  text: string;
  topics: Array<{ id: string; title: string; sortOrder?: number }>;
}

export interface ContentPacketConfirmedChapter {
  chapterId: string;
  title: string;
  sortOrder: number;
  startPage?: number | null;
  endPage?: number | null;
  topicCount: number;
  hasFigures: boolean;
  figureCount: number;
  topics?: Array<{ id: string; title: string; sortOrder?: number }>;
}

export interface ContentPacketResponse {
  resourceId: string;
  resourceTitle: string;
  courseCode?: string | null;
  courseName?: string | null;
  totalChapters: number;
  confirmedChapterSplit: ContentPacketConfirmedChapter[];
  chaptersWithFigures: string[];
  chaptersWithoutFigures?: string[];
  totalFigures: number;
  figuresNote?: string;
  chapters: ContentPacketChapter[];
}

export interface ManualExportPacketResponse {
  resourceId: string;
  chapterId: string;
  topicId?: string | null;
  metadata: {
    courseId?: string | null;
    courseCode?: string;
    courseName?: string;
    moduleId: string;
    moduleTitle: string;
    chapterId: string;
    chapterTitle: string;
    topicId?: string | null;
    topicTitle?: string;
    language: string;
    subjectProfile: string;
    startPage?: number | null;
    endPage?: number | null;
    topics: Array<{ id: string; title: string; sortOrder?: number }>;
  };
  requestedCounts: {
    totalQuestions: number;
    easy: number;
    medium: number;
    hard: number;
    easyPct: number;
    mediumPct: number;
    hardPct: number;
  };
  hasFigures: boolean;
  figureCount: number;
  figures: ContentFigure[];
  chapterText: string;
  schemas: {
    content: unknown;
    notes: unknown;
    quiz: unknown;
  };
  templates: {
    content: unknown;
    notes: unknown;
    quiz: unknown;
  };
  llmPrompt: string;
}

export interface ReviewQueueItem {
  type: "quiz" | "content" | "notes";
  id: string;
  cacheRowId?: string;
  questionIndex?: number;
  status: "draft" | "flagged" | "active" | "retired";
  difficulty?: string;
  chapterTitle?: string;
  topicTitle?: string;
  question?: string;
  options?: string[];
  correct?: number;
  explanation?: string;
  sourceReference?: string;
  reportCount?: number;
  reports?: Array<{ reason: string; createdAt?: string }>;
  markdown?: string;
  version?: number;
  pages?: number[];
  figures?: ContentFigure[];
  keyTerms?: Array<{ term: string; definition: string }>;
  source?: string;
  batchId?: string;
  createdAt?: string;
  figure_status?: "none" | "required" | "uploaded";
  figure_image?: string | null;
  figure_alt?: string | null;
  figure_location?: {
    source: string;
    pdfPage: number;
    region?: string;
    label?: string;
    description?: string;
    bbox?: number[];
  } | null;
}

export interface ReviewQueueResponse {
  items: ReviewQueueItem[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  filters: {
    kind: string;
    status: string[];
    chapterId?: string | null;
    topicId?: string | null;
    batchId?: string | null;
  };
}

export interface ReviewStatsResponse {
  questions: {
    total: number;
    byStatus: Record<string, number>;
    byDifficulty: Record<string, number>;
    batchCount: number;
  };
  content: {
    totalVersions: number;
    byStatus: Record<string, number>;
  };
  notes: {
    totalVersions: number;
    byStatus: Record<string, number>;
  };
  scope: {
    chapterId?: string | null;
    topicId?: string | null;
    moduleId?: string | null;
  };
}

export interface GenerationJobItem {
  id: string;
  status: "pending" | "running" | "completed" | "failed" | "cancelled";
  stage: string;
  attempts: number;
  scope?: {
    resourceId?: string;
    chapterId?: string;
    topicId?: string | null;
    kind?: string;
    targetCount?: number;
    verifyAnswers?: boolean;
    adminId?: string | null;
  };
  lastError?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export type CatalogUniversity = {
  id: string;
  name: string;
  short_name: string;
  region?: string | null;
  isActive: boolean;
  resourceCount: number;
  studentCount: number;
};

export type CatalogCourse = {
  id: string;
  name: string;
  code: string;
  description?: string | null;
  imageUrl?: string | null;
  streamId?: string | null;
  streamName?: string | null;
  semester?: number | null;
  isPremium?: boolean;
  is_premium?: boolean;
  courseType?: string | null;
  course_type?: string | null;
  extractionProfile?: string | null;
  extraction_profile?: string | null;
  resourceCount: number;
};

export type CatalogStream = {
  id: string;
  name: string;
  code: string;
  description?: string | null;
  isActive: boolean;
  resourceCount: number;
  studentCount: number;
  createdAt?: string | null;
};

export type SocialLink = {
  id: string;
  platform: string;
  title: string;
  url: string;
  icon?: string | null;
  isActive: boolean;
  sortOrder: number;
  createdAt?: string;
  updatedAt?: string;
};

export type AdminAccount = {
  id: string;
  fullName: string;
  phone: string;
  isActive: boolean;
  isSuperAdmin: boolean;
  permissions: string[];
  telegramUserId?: number | null;
  pendingSetup?: boolean;
  createdAt?: string | null;
};

export type AdminResource = {
  id: string;
  title: string;
  subject: string;
  type: string;
  size: string;
  date: string;
  locked: boolean;
  pages: number;
  access: "Free" | "Premium";
  ingestionStatus: string;
  ingestionError?: string | null;
  storageMode?: string;
  questionCount?: number | null;
  resourceType?: string;
  canReingest?: boolean;
  canStop?: boolean;
  canContinue?: boolean;
  courseId?: string | null;
  universityId?: string | null;
  streamId?: string | null;
  streamName?: string | null;
  isFree?: boolean;
  examYear?: string | null;
  isPublished?: boolean;
  examClass?: "final" | "mid" | "coc" | null;
  publishedAt?: string | null;
  extractionStatus?: "not_requested" | "queued" | "running" | "extracted" | "published" | "rejected" | "failed" | string;
  extractionError?: string | null;
  contentPublished?: boolean;
  views?: number;
  uniqueViewers?: number;
  downloads?: number;
  practices?: number;
  lastAccessAt?: string | null;
};

export type AdminPayment = {
  id: string;
  paymentId?: string;
  txnId?: string;
  user: string;
  amount: string;
  amountRaw?: number;
  plan: string;
  semester?: number | null;
  packageName?: string | null;
  date: string;
  status: "Confirmed" | "Pending" | "Pending Review" | "Failed" | string;
  provider?: string;
  confirmationSource?: string | null;
  reviewReason?: string | null;
  receiptUrl?: string | null;
};


export type AdminActivity = {
  user: string;
  action: string;
  time: string;
  type: "upgrade" | "register" | "quiz" | "error" | "download";
};

export type AdminLogEntry = {
  id: string;
  category: "users" | "resources" | "payments" | "ai" | "system" | "access" | "error" | "crucial" | string;
  severity: "info" | "success" | "warning" | "error" | "crucial" | string;
  title: string;
  action?: string;
  detail: string;
  actor: string;
  entityType?: string | null;
  entityId?: string | null;
  endpoint?: string | null;
  statusCode?: number | null;
  errorCode?: string | null;
  userId?: string | null;
  userPhone?: string | null;
  details?: Record<string, unknown> | null;
  createdAt: string;
  time: string;
};


export type AdminListCounts = Record<string, number>;

export type StudentExam = {
  id: string;
  year: string;
  title: string;
  subject?: string;
  subjects: number;
  questions: number;
  locked: boolean;
  examClass?: "final" | "mid" | "coc" | null;
  university?: string | null;
};

export type StudentResource = {
  id: string;
  title: string;
  subject: string;
  courseId?: string | null;
  type: string;
  fileType?: string;
  size: string;
  date: string;
  locked: boolean;
  pages: number;
  contentPublished?: boolean;
};

export type CocQuestion = {
  id?: string;
  text: string;
  options: string[];
  correct_answer: number;
  explanation?: string | null;
  passage_id?: string | null;
  passageId?: string | null;
  passage_key?: string | null;
  passageKey?: string | null;
  figure_status?: "none" | "required" | "uploaded";
  figure_image?: string | null;
  figure_alt?: string | null;
  figure_location?: {
    source: string;
    pdfPage: number;
    region?: string;
    label?: string;
    description?: string;
    bbox?: number[];
  } | null;
};

export type CocExam = {
  id: string;
  title: string;
  category: string;
  year?: string | null;
  description?: string | null;
  time_limit_minutes?: number | null;
  pass_percentage: number;
  is_published: boolean;
  is_free: boolean;
  question_count: number;
  questions: CocQuestion[];
  passages?: Record<string, { id?: string; title?: string | null; text?: string; body?: string }>;
  access?: "full" | "preview" | "locked";
  locked_count?: number;
  is_preview?: boolean;
  preview_limit?: number | null;
  stream_id?: string | null;
  semester: number;
  stream_name?: string | null;
  message?: string | null;
  created_at: string;
};

export type CocQuestionResult = {
  id: string;
  text: string;
  options: string[];
  selected_answer?: number | null;
  correct_answer: number;
  is_correct: boolean;
  explanation?: string | null;
  passage_id?: string | null;
  passageId?: string | null;
};

export type CocScoreResponse = {
  exam_id: string;
  title: string;
  category: string;
  total_questions: number;
  answered_count: number;
  correct_count: number;
  wrong_count: number;
  unanswered_count: number;
  score_percentage: number;
  pass_percentage: number;
  passed: boolean;
  access: "full" | "preview" | "locked";
  is_preview: boolean;
  preview_limit?: number | null;
  questions: CocQuestionResult[];
  passages?: Record<string, { id?: string; title?: string | null; text?: string; body?: string }>;
  message?: string | null;
};

export type CocExamCreate = {
  title: string;
  category: string;
  year?: string;
  description?: string;
  time_limit_minutes?: number;
  pass_percentage?: number;
  is_published?: boolean;
  is_free?: boolean;
  stream_id?: string | null;
  semester?: number;
  questions?: CocQuestion[];
  passages?: Array<{ key?: string; title?: string | null; text: string }>;
};

export type AdminCourseListItem = {
  id: string;
  quizAccess?: number;
  examAccess?: number;
  cocAccess?: number;
  totalAccess?: number;
  name: string;
  code: string;
  description?: string | null;
  streamId?: string | null;
  streamName?: string | null;
  semester?: number | null;
  isPremium?: boolean;
  is_premium?: boolean;
  imageUrl?: string | null;
  courseType?: string | null;
  course_type?: string | null;
  extractionProfile?: string | null;
  extraction_profile?: string | null;
  resourceCount: number;
  draftCount: number;
};

export type AdminCourseModuleItem = {
  id: string;
  title: string;
  type: "Module" | "Handout";
  resourceType: string;
  status: "Draft" | "Visible" | "Hidden" | "Archived";
  rawStatus: "draft" | "published" | "hidden" | "archived";
  isPublished: boolean;
  isHidden: boolean;
  isArchived?: boolean;
  isFree?: boolean;
  studentUrl?: string | null;
  rawUrl?: string | null;
  date: string;
  pageCount: number;
  fileSize: number;
  fileName?: string | null;
  telegramFileId?: string | null;
};

export type AdminCourseExamItem = {
  id: string;
  title: string;
  type: "Past exam";
  examYear: string;
  examClass?: string | null;
  status: "Draft" | "Visible" | "Hidden" | "Archived";
  rawStatus: "draft" | "published" | "hidden" | "archived";
  isPublished: boolean;
  isHidden: boolean;
  isArchived?: boolean;
  isFree?: boolean;
  studentUrl?: string | null;
  rawUrl?: string | null;
  date: string;
  questionCount: number;
  source?: string | null;
};

export type AdminCourseQuizItem = {
  id: string;
  title: string;
  topic: string;
  difficulty: string;
  status: "Draft" | "Visible" | "Hidden";
  rawStatus: "draft" | "published" | "hidden";
  isPublished: boolean;
  isHidden: boolean;
  isFree?: boolean;
  studentUrl?: string | null;
  rawUrl?: string | null;
  date: string;
  questionCount: number;
  questions?: any[];
  moduleId?: string | null;
  chapterId?: string | null;
  origin?: string | null;
  source?: string | null;
  model?: string | null;
};

export type AdminCourseNoteItem = {
  id: string;
  title: string;
  moduleTitle: string;
  chapterTitle: string;
  kind: "note" | "content";
  status: "Draft" | "Visible" | "Hidden";
  rawStatus: "draft" | "published" | "hidden";
  isPublished: boolean;
  isHidden: boolean;
  isFree?: boolean;
  studentUrl?: string | null;
  rawUrl?: string | null;
  date: string;
  markdown?: string;
  source?: string | null;
  model?: string | null;
};

export type AdminCourseReviewFlag = {
  type: "blocking" | "advisory";
  text: string;
};

export type AdminCourseReviewItem = {
  id: string;
  itemType: "module" | "exam" | "quiz" | "note" | "content";
  label: string;
  type: string;
  source: string;
  time: string;
  status: "draft";
  flags: AdminCourseReviewFlag[];
  hasBlocking: boolean;
  previewData: any;
};

export type AdminCourseOrderChapter = {
  id: string;
  idx: number;
  title: string;
  start_page: number;
  end_page: number;
  include: boolean;
  warnings?: string[];
  status?: string;
};

export type AdminCourseOrderReviewUnit = {
  id: string;
  stage: string;
  chapterId?: string | null;
  error: string;
};

export type AdminCourseOrderItem = {
  id: string;
  documentId: string;
  moduleId: string;
  moduleTitle: string;
  state: string;
  stateText: string;
  actionText: string | null;
  progress: string;
  counts: {
    notes: number;
    questions: number;
    toReview: number;
  };
  tokens: number;
  estimatedCost: string;
  lastMessage?: string | null;
  chapters: AdminCourseOrderChapter[];
  reviewUnits: AdminCourseOrderReviewUnit[];
};

export type AdminCourseMonthlyUsage = {
  monthlyTokens: number;
  monthlyBudget: number;
  estimatedCost: string;
  budgetPct: number;
};

export type AdminCourseDetailResponse = {
  course: {
    id: string;
    name: string;
    code: string;
    description?: string | null;
    streamId?: string | null;
    streamName?: string | null;
    semester?: number | null;
    isPremium?: boolean;
    is_premium?: boolean;
    imageUrl?: string | null;
    courseType?: string | null;
    course_type?: string | null;
    extractionProfile?: string | null;
    extraction_profile?: string | null;
  };
  modules: AdminCourseModuleItem[];
  exams: AdminCourseExamItem[];
  quizzes: AdminCourseQuizItem[];
  notes: AdminCourseNoteItem[];
  /** Chapters listed in the Chapters tab and items waiting in Content review; from the same queries as those lists. */
  chaptersCount: number;
  ingestPending: number;
  reviews: AdminCourseReviewItem[];
  orders: AdminCourseOrderItem[];
  monthlyUsage: AdminCourseMonthlyUsage;
};

export type StudySubjectItem = {
  id: string;
  code: string;
  courseName: string;
  subject: string;
  slug: string;
  name: string;
  imageUrl?: string | null;
  resourceCount?: number;
  semester?: number;
  streamId?: string | null;
  streamName?: string | null;
  stream?: "common" | "natural" | "social" | string;
  counts?: {
    modules?: number;
    quiz?: number;
    exams?: number;
    notes?: number;
  };
};

export type PremiumPackage = {
  id: string;
  name: string;
  description?: string | null;
  planType?: "monthly" | "semester";
  plan_type?: "monthly" | "semester";
  semestersCovered?: number[];
  semesters_covered?: number[];
  stream?: "common" | "natural" | "social" | string | null;
  price: number;
  durationMonths?: number;
  duration_months?: number;
  features: string[];
  badgeText?: string | null;
  badge_text?: string | null;
  isRecommended?: boolean;
  is_recommended?: boolean;
  isActive?: boolean;
  is_active?: boolean;
  displayOrder?: number;
  display_order?: number;
  original_price?: number | null;
  originalPrice?: number | null;
  discount_price?: number | null;
  discountPrice?: number | null;
  discount_badge?: string | null;
  discountBadge?: string | null;
  is_discounted?: boolean;
  isDiscounted?: boolean;
};

export type Entitlement = {
  id: string;
  userId?: string;
  user_id?: string;
  stream: string;
  semester: number;
  packageId?: string | null;
  package_id?: string | null;
  plan: string;
  startsAt?: string;
  starts_at?: string;
  expiresAt?: string;
  expires_at?: string;
  status: "active" | "expired" | "revoked";
  isActive?: boolean;
  is_active?: boolean;
};

export type AdminPremiumPackage = PremiumPackage & {
  entitlementCount?: number;
  createdAt?: string | null;
  updatedAt?: string | null;
};

export type AdminPackageCreate = {
  name: string;
  description?: string;
  plan_type: string;
  semesters_covered: number[];
  stream?: string | null;
  price: number;
  duration_months: number;
  features: string[];
  is_active: boolean;
  display_order: number;
};

export type AdminEntitlementItem = {
  id: string;
  userId: string;
  userName: string;
  userPhone: string;
  stream: string;
  semester: number;
  packageId?: string | null;
  packageName: string;
  plan: string;
  startsAt: string;
  expiresAt: string;
  status: "active" | "expired" | "revoked";
  createdAt?: string | null;
};

export type SemesterStatus = {
  semester: number;
  unlocked: boolean;
  expiresAt?: string | null;
  expires_at?: string | null;
  courseCount?: number;
  course_count?: number;
  lowestPackagePrice?: number | null;
  lowest_package_price?: number | null;
  status?: string;
  isAvailable?: boolean;
  is_available?: boolean;
  hasPendingRequest?: boolean;
  has_pending_request?: boolean;
  pendingRequestStatus?: string | null;
  pending_request_status?: string | null;
  from_price_etb?: number | null;
  is_fully_premium?: boolean;
  isFullyPremium?: boolean;
};

export type ChartPoint = { day: string; users: number };
export type CurriculumTopic = { id: string; title: string; confidence?: string };
export type CurriculumChapter = {
  id: string;
  title: string;
  isDefault?: boolean;
  confidence?: string;
  topics: CurriculumTopic[];
};
export type ModuleCurriculum = {
  courseId: string;
  courseName?: string | null;
  courseCode?: string | null;
  moduleId: string;
  moduleTitle?: string;
  chapters: CurriculumChapter[];
};

export interface TopicNoteReaderResponse {
  course: {
    id: string | null;
    name: string;
    code: string;
    subject: string;
  };
  module: {
    id: string;
    title: string;
  };
  chapter: {
    id: string;
    title: string;
    index: number;
    total: number;
  };
  topic: {
    id: string;
    title: string;
    notes: string;
    keyTerms?: Array<{ term: string; definition: string }>;
  };
  position: {
    current: number;
    total: number;
  };
  prev: {
    topicId: string;
    title: string;
    chapterTitle: string;
    startsNewChapter: boolean;
  } | null;
  next: {
    topicId: string;
    title: string;
    chapterTitle: string;
    startsNewChapter: boolean;
  } | null;
  access: "full" | "preview" | "locked";
  isLocked?: boolean;
  requiresPremium?: boolean;
}
export type PlanSlice = { name: string; value: number };
export type SubjectCount = { subject: string; count: number };

export class ApiError extends Error {
  status: number;
  code?: string;
  requestId?: string;
  constructor(status: number, message: string, code?: string, requestId?: string) {
    super(message);
    this.status = status;
    this.code = code;
    this.requestId = requestId;
  }
}

const _recentClientErrors = new Map<string, number>();

export function reportWebError(payload: {
  message: string;
  source?: string;
  level?: string;
  stack_trace?: string;
  exception_type?: string;
  request_id?: string;
  url?: string;
  context?: Record<string, unknown>;
}, token?: string | null) {
  const distinctKey = `${payload.exception_type || "Error"}:${payload.message}:${payload.url || ""}`;
  const now = Date.now();
  const lastSent = _recentClientErrors.get(distinctKey);
  if (lastSent && now - lastSent < 60_000) {
    return;
  }
  _recentClientErrors.set(distinctKey, now);

  const truncatedPayload = {
    ...payload,
    source: payload.source || "web",
    level: payload.level || "error",
    stack_trace: payload.stack_trace ? payload.stack_trace.slice(0, 4000) : undefined,
    message: payload.message ? payload.message.slice(0, 1000) : "Unknown client error",
  };

  fetch(`${API_BASE}/logs/client`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(truncatedPayload).slice(0, 8192),
  }).catch(() => {});
}

if (typeof window !== "undefined") {
  window.addEventListener("error", (event) => {
    reportWebError({
      message: event.message || "Uncaught window error",
      exception_type: event.error?.name || "WindowError",
      stack_trace: event.error?.stack,
      url: window.location.pathname,
      context: { filename: event.filename, lineno: event.lineno, colno: event.colno },
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    const msg = reason instanceof Error ? reason.message : String(reason);
    const stack = reason instanceof Error ? reason.stack : undefined;
    reportWebError({
      message: msg || "Unhandled Promise Rejection",
      exception_type: reason instanceof Error ? reason.name : "UnhandledRejection",
      stack_trace: stack,
      url: window.location.pathname,
    });
  });
}

type AuthBridge = {
  getTokens: () => AuthTokens | null;
  refreshSession: () => Promise<AuthTokens | null>;
  logout: () => void;
  getAccessToken: () => Promise<string | null>;
  isLocked: () => boolean;
};

/** Send the user home after a hard sign-out. Never while the admin lock screen is up (keeps URL and page state). */
function redirectHome() {
  if (authBridge?.isLocked()) return;
  if (typeof window !== "undefined" && window.location.pathname !== "/" && !window.location.pathname.startsWith("/auth")) {
    window.location.href = "/";
  }
}

let authBridge: AuthBridge | null = null;

export function bindAuth(bridge: AuthBridge | null) {
  authBridge = bridge;
}

async function ensureValidToken(token: string): Promise<string> {
  if (authBridge?.isLocked()) throw new ApiError(423, "Locked");
  const stored = authBridge?.getTokens();
  if (!stored) return token;
  if (isIdleExpired(stored)) {
    authBridge?.logout();
    redirectHome();
    throw new ApiError(401, "Session expired");
  }
  const active = stored.accessToken === token ? stored : stored;
  if (!isAccessExpired(active)) return active.accessToken;
  const refreshed = await authBridge?.refreshSession();
  if (!refreshed) {
    authBridge?.logout();
    redirectHome();
    throw new ApiError(401, "Session expired");
  }
  return refreshed.accessToken;
}

export async function request<T>(
  path: string,
  options: RequestInit & { _retried?: boolean; _silentPremium?: boolean } = {},
  token?: string | null,
): Promise<T> {
  const { _retried, _silentPremium, ...fetchOptions } = options;
  const headers: Record<string, string> = {
    ...(fetchOptions.body instanceof FormData ? {} : { "Content-Type": "application/json" }),
    ...(fetchOptions.headers as Record<string, string> | undefined),
  };

  let authToken = token;
  if (authToken) {
    try {
      authToken = await ensureValidToken(authToken);
    } catch (err) {
      throw err;
    }
    headers.Authorization = `Bearer ${authToken}`;
  }

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, { credentials: "include", ...fetchOptions, headers });
  } catch (err) {
    reportWebError(
      {
        message: "Unable to connect to the server (Network error)",
        exception_type: "NetworkError",
        url: path,
      },
      authToken,
    );
    if (err instanceof ApiError) throw err;
    throw new ApiError(0, "Unable to connect to the server. Please check your internet connection and try again.");
  }

  const reqId = res.headers.get("X-Request-ID") || undefined;

  if (res.status === 401 && authToken && !_retried && authBridge) {
    const refreshed = await authBridge.refreshSession();
    if (refreshed) {
      return request<T>(path, { ...fetchOptions, _retried: true, _silentPremium }, refreshed.accessToken);
    }
    authBridge.logout();
    redirectHome();
  }

  if (!res.ok) {
    let message = res.statusText || "Request failed";
    let code: string | undefined;
    try {
      const body = await res.json();
      code = body.code || (typeof body.detail === "object" ? body.detail?.code : undefined);
      if (typeof body.message === "string") message = body.message;
      else if (typeof body.detail === "object" && body.detail !== null && "message" in body.detail) {
        message = String((body.detail as { message?: string }).message ?? res.statusText);
      } else if (body.detail) message = body.detail;
      else if (body.error && typeof body.error === "string") message = body.error;
      if (Array.isArray(message)) message = message.map((e: { msg?: string }) => e.msg ?? "").join(", ");
    } catch { /* ignore */ }
    if (!_silentPremium && res.status === 403 && (code === "premium_required" || /premium/i.test(String(message)))) {
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("soorgaa:premium_required"));
      }
    }
    if (res.status >= 500) {
      reportWebError(
        {
          message: String(message || "Internal server error"),
          exception_type: "Api5xxError",
          url: path,
          request_id: reqId,
          context: { status: res.status, code },
        },
        authToken,
      );
    }
    throw new ApiError(res.status, String(message || "Request failed"), code, reqId);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

async function requestBlob(path: string, token: string): Promise<Blob> {
  let authToken: string;
  try {
    authToken = await ensureValidToken(token);
  } catch (err) {
    throw err instanceof ApiError ? err : new ApiError(401, "Session expired");
  }

  const doFetch = async (bearer: string, retried = false): Promise<Response> => {
    let res: Response;
    try {
      res = await fetch(`${API_BASE}${path}`, {
        headers: { Authorization: `Bearer ${bearer}` },
      });
    } catch (err) {
      if (err instanceof ApiError) throw err;
      throw new ApiError(0, "Unable to download file. Please check your internet connection.");
    }
    if (res.status === 401 && !retried && authBridge) {
      const refreshed = await authBridge.refreshSession();
      if (refreshed) return doFetch(refreshed.accessToken, true);
      authBridge.logout();
    }
    return res;
  };

  const res = await doFetch(authToken);
  if (!res.ok) {
    let message = res.statusText;
    let code: string | undefined;
    try {
      const body = await res.json();
      code = body.code || (typeof body.detail === "object" ? body.detail?.code : undefined);
      message = body.detail ?? body.message ?? message;
      if (Array.isArray(message)) message = message.map((e: { msg?: string }) => e.msg ?? "").join(", ");
    } catch { /* ignore — not JSON */ }
    if (res.status === 403 && (code === "premium_required" || /premium/i.test(String(message)))) {
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("soorgaa:premium_required"));
      }
    }
    throw new ApiError(res.status, String(message), code);
  }
  return res.blob();
}

export const api = {
  resolveImageUrl,
  requestCode: (phone: string) =>
    request<{
      session_token: string;
      bot_url: string;
      phone: string | null;
      flow: AuthFlow;
      code_sent: boolean;
      is_registered: boolean | null;
      message: string;
    }>("/auth/request-code", {
      method: "POST",
      body: JSON.stringify({ phone }),
    }),

  resendCode: (session_token: string) =>
    request<{ message: string }>("/auth/resend-code", {
      method: "POST",
      body: JSON.stringify({ session_token }),
    }),

  startSession: (phone?: string) =>
    request<{
      session_token: string;
      bot_url: string;
      phone: string | null;
      is_registered: boolean | null;
    }>("/auth/session", {
      method: "POST",
      body: JSON.stringify({ phone: phone || null }),
    }),

  pollSession: (token: string) => request<SessionStatus>(`/auth/session/${token}`),

  startTelegramLink: () =>
    request<{ link_token: string; bot_url: string }>("/auth/telegram/start-link", { method: "POST" }),

  pollTelegramLink: (link_token: string) =>
    request<{
      status: "pending" | "completed" | "expired";
      telegram_user_id?: number;
      telegram_auth_token?: string;
      first_name?: string;
      username?: string;
    }>(`/auth/telegram/poll-link?token=${encodeURIComponent(link_token)}`),

  register: (payload: {
    session_token: string;
    full_name: string;
    university_id: string;
    stream_id?: string;
    department?: string;
    preferred_language?: string;
  }) =>
    request<{
      access_token: string;
      refresh_token: string;
      role: UserRole;
      access_expires_at: number;
      idle_expires_at?: number | null;
    }>("/auth/register", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  login: (session_token: string, code: string) =>
    request<{
      access_token: string;
      refresh_token: string;
      role: UserRole;
      access_expires_at: number;
      idle_expires_at?: number | null;
    }>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ session_token, code }),
    }),

  adminVerifyPassword: (payload: {
    session_token?: string;
    phone?: string;
    password: string;
    device_id?: string;
    device_token?: string;
    device_name?: string;
  }) =>
    request<{
      session_token?: string;
      flow: string;
      code_sent: boolean;
      message: string;
      access_token?: string;
      refresh_token?: string;
      role?: UserRole;
      access_expires_at?: number;
      idle_expires_at?: number | null;
      is_super_admin?: boolean;
      permissions?: string[];
      device_token?: string;
    }>("/auth/admin/verify-password", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  adminVerifyOtp: (
    session_token_or_payload: string | { session_token: string; code: string; device_id?: string; device_name?: string },
    code?: string,
    device_id?: string,
    device_name?: string,
  ) => {
    const body = typeof session_token_or_payload === "string"
      ? { session_token: session_token_or_payload, code: code || "", device_id, device_name }
      : session_token_or_payload;
    return request<{
      access_token: string;
      refresh_token: string;
      role: UserRole;
      access_expires_at: number;
      idle_expires_at?: number | null;
      is_super_admin?: boolean;
      permissions?: string[];
      device_token?: string;
    }>("/auth/admin/verify-otp", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  adminLogout: (device_id?: string, device_token?: string, token?: string, refresh_token?: string) =>
    request<{ ok: boolean; message: string }>("/auth/admin/logout", {
      method: "POST",
      body: JSON.stringify({ device_id, device_token, refresh_token }),
    }, token),

  adminLogin: (payload: { phone: string; password: string; device_id?: string; device_token?: string; device_name?: string }) =>
    request<{
      session_token?: string;
      flow: string;
      code_sent: boolean;
      message: string;
      access_token?: string;
      refresh_token?: string;
      role?: UserRole;
      access_expires_at?: number;
      idle_expires_at?: number | null;
      is_super_admin?: boolean;
      permissions?: string[];
      device_token?: string;
      pending_setup?: boolean;
    }>("/auth/admin/login", {
      method: "POST",
      body: JSON.stringify(payload),
    }),


  adminSetupStatus: (params: { phone?: string; session_token?: string }) => {
    const qs = new URLSearchParams();
    if (params.phone) qs.set("phone", params.phone);
    if (params.session_token) qs.set("session_token", params.session_token);
    return request<{
      phone: string;
      role: string;
      isAdmin: boolean;
      setupPending: boolean;
      hasTelegram: boolean;
    }>(`/auth/admin/setup-status?${qs.toString()}`);
  },

  adminSetupInitiate: (body: { phone?: string; session_token?: string }) =>
    request<{
      session_token: string;
      flow: string;
      code_sent: boolean;
      message: string;
      setup_pending?: boolean;
    }>("/auth/admin/setup-initiate", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  adminSetupPassword: (body: { session_token: string; code: string; password: string }) =>
    request<{
      ok: boolean;
      message: string;
      requires_login?: boolean;
      access_token?: string;
      refresh_token?: string;
      role?: UserRole;
      access_expires_at?: number;
      idle_expires_at?: number | null;
      is_super_admin?: boolean;
      permissions?: string[];
    }>("/auth/admin/setup-password", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  refreshSession: (refresh_token: string, active = true) =>
    request<{
      access_token: string;
      refresh_token: string;
      role: UserRole;
      access_expires_at: number;
      idle_expires_at?: number | null;
      idle_lock_minutes?: number | null;
      is_super_admin?: boolean;
      permissions?: string[];
    }>("/auth/refresh", {
      method: "POST",
      body: JSON.stringify({ refresh_token, active }),
    }),

  universities: () => request<University[]>("/catalog/universities"),

  streams: () => request<CatalogStream[]>("/catalog/streams"),

  courses: () =>
    request<{ id: string; name: string; code: string; imageUrl?: string | null; description?: string | null }[]>("/catalog/courses").then(
      (items) => (items || []).map((c) => ({ ...c, imageUrl: resolveImageUrl(c.imageUrl) }))
    ),

  feedback: {
    promptStatus: (token: string) =>
      request<{
        showPrompt: boolean;
        reason?: string | null;
        title?: string;
        message?: string;
      }>("/feedback/prompt-status", {}, token),

    submit: (
      token: string,
      body: { rating: number; comment?: string; trigger_type?: string },
    ) =>
      request<{ success: boolean; message: string }>("/feedback", {
        method: "POST",
        body: JSON.stringify(body),
      }, token),

    dismiss: (token: string) =>
      request<{ success: boolean }>("/feedback/dismiss", {
        method: "POST",
      }, token),

    publicList: (limit: number = 10) =>
      request<{
        id: string;
        rating: number;
        comment: string | null;
        displayName: string;
        createdAt: string;
      }[]>(`/feedback/public?limit=${limit}`),
  },

  socialLinks: () => request<SocialLink[]>("/catalog/social-links"),

  profile: (token: string) =>
    request<{
      fullName: string;
      phone: string;
      university: string | null;
      department: string | null;
      streamId?: string | null;
      streamName?: string | null;
      streamCode?: string | null;
      currentSemester?: number;
      canChangeStream?: boolean;
      isPremium: boolean;
      avatar: string;
      role?: string;
      pendingAdminSetup?: boolean;
      preferredLanguage?: "en" | "am" | "oro";
    }>("/profile/me", {}, token),

  updateProfile: (
    token: string,
    body: {
      fullName?: string;
      streamId?: string;
      currentSemester?: number;
      preferredLanguage?: "en" | "am" | "oro";
    },
  ) =>
    request<{
      fullName: string;
      phone: string;
      university: string | null;
      department: string | null;
      streamId?: string | null;
      streamName?: string | null;
      streamCode?: string | null;
      currentSemester?: number;
      canChangeStream?: boolean;
      isPremium: boolean;
      avatar: string;
    }>("/profile/me", {
      method: "PATCH",
      body: JSON.stringify({
        full_name: body.fullName,
        stream_id: body.streamId,
        current_semester: body.currentSemester,
        preferred_language: body.preferredLanguage,
      }),
    }, token),

  changePassword: (
    token: string,
    body: { currentPassword?: string; newPassword: string },
  ) =>
    request<{ message: string }>("/profile/change-password", {
      method: "POST",
      body: JSON.stringify({
        current_password: body.currentPassword,
        new_password: body.newPassword,
      }),
    }, token),

  payments: {
    plans: () =>
      request<{
        monthly: { amountEtb: number; label: string };
        semester: { amountEtb: number; label: string; savings?: string };
        features?: string[];
        chapaConfigured?: boolean;
        telebirrConfigured?: boolean;
        chapaEnabled?: boolean;
        telebirrEnabled?: boolean;
        providers?: { chapa: boolean; telebirr: boolean };
        anyGateway?: boolean;
        preferredProvider?: string;
        telebirrManualPhone?: string;
        telebirrManualName?: string;
        telebirrManualEnabled?: boolean;
        cbeAccountNumber?: string;
        cbeAccountName?: string;
        cbeReceiveLink?: string;
        cbeManualEnabled?: boolean;
        manualInstructions?: string;
        manualPaymentEnabled?: boolean;
        fastestPaymentMethod?: string;
        expressCheckoutEnabled?: boolean;
        packageDiscounts?: Record<string, {
          discount_price?: number;
          original_price?: number;
          discount_badge?: string;
          is_active?: boolean;
        }>;
      }>("/payments/plans"),

    packages: () =>
      request<PremiumPackage[]>("/packages"),

    entitlements: (token: string) =>
      request<Entitlement[]>("/entitlements", {}, token),

    semesterStatus: (token: string) =>
      request<SemesterStatus[]>("/semester-status", {}, token),

    initiate: (
      token: string,
      plan: "monthly" | "semester",
      provider?: "chapa" | "telebirr" | "auto",
      packageId?: string,
      semester?: number,
      idempotencyKey?: string,
    ) =>
      request<{
        checkoutUrl: string | null;
        txRef?: string;
        message: string;
        plan: string;
        amountEtb: number;
        features: string[];
        provider?: string;
        packageId?: string;
        fallbackUsed?: boolean;
        code?: string;
        chapaConfigured?: boolean;
        providers?: { chapa: boolean; telebirr: boolean };
      }>("/payments/initiate", {
        method: "POST",
        body: JSON.stringify({
          plan,
          ...(provider ? { provider } : {}),
          ...(packageId ? { package_id: packageId, packageId } : {}),
          ...(semester ? { semester } : {}),
          ...(idempotencyKey ? { idempotency_key: idempotencyKey } : {}),
        }),
      }, token),

    status: (token: string, txRef: string, verify = true) =>
      request<{
        txRef: string;
        status: "pending" | "success" | "failed";
        plan: string;
        amountEtb: number;
        provider: string;
        isPremium: boolean;
        premiumUntil: string | null;
        confirmedAt: string | null;
        message: string;
      }>(`/payments/status?tx_ref=${encodeURIComponent(txRef)}&verify=${verify ? "true" : "false"}`, {}, token),

    verifyTelebirrManual: (
      token: string,
      txnId: string,
      plan: "monthly" | "semester" = "monthly",
      packageId?: string,
      semester?: number,
      idempotencyKey?: string,
    ) =>
      request<{
        status: string;
        message: string;
        isPremium: boolean;
        txRef?: string;
        txnId?: string;
        plan?: string;
        amountEtb?: number;
        packageId?: string;
      }>("/payments/telebirr-manual/verify", {
        method: "POST",
        body: JSON.stringify({
          txn_id: txnId,
          plan,
          ...(packageId ? { package_id: packageId, packageId } : {}),
          ...(semester ? { semester } : {}),
          ...(idempotencyKey ? { idempotency_key: idempotencyKey } : {}),
        }),
      }, token),

    verifyCbeManual: (
      token: string,
      receiptLink: string,
      plan: "monthly" | "semester" = "monthly",
      packageId?: string,
      semester?: number,
      idempotencyKey?: string,
    ) =>
      request<{
        status: string;
        message: string;
        isPremium: boolean;
        txRef?: string;
        txnId?: string;
        plan?: string;
        amountEtb?: number;
        receiptAmount?: number;
        receiptDate?: string;
        receiver?: { name: string; account: string };
        packageId?: string;
      }>("/payments/cbe-manual/verify", {
        method: "POST",
        body: JSON.stringify({
          receipt_link: receiptLink,
          plan,
          ...(packageId ? { package_id: packageId, packageId } : {}),
          ...(semester ? { semester } : {}),
          ...(idempotencyKey ? { idempotency_key: idempotencyKey } : {}),
        }),
      }, token),

    submitCbeManual: (
      token: string,
      txnId: string,
      plan: "monthly" | "semester" = "monthly",
      packageId?: string,
      semester?: number,
      idempotencyKey?: string,
    ) =>
      request<{
        status: string;
        message: string;
        txRef?: string;
        txnId?: string;
        plan?: string;
        amountEtb?: number;
        cbeReceiveLink?: string;
        packageId?: string;
      }>("/payments/cbe-manual/submit", {
        method: "POST",
        body: JSON.stringify({
          txn_id: txnId,
          plan,
          ...(packageId ? { package_id: packageId, packageId } : {}),
          ...(semester ? { semester } : {}),
          ...(idempotencyKey ? { idempotency_key: idempotencyKey } : {}),
        }),
      }, token),

    getPending: (token: string, semester?: number) =>
      request<{
        hasPending: boolean;
        payment: {
          id: string;
          txRef: string;
          txnId: string;
          plan: string;
          amount: number;
          amountEtb: number;
          date: string | null;
          status: string;
          semester?: number;
          stream?: string;
          packageId?: string | null;
          packageName?: string | null;
          provider?: string;
          isChapa?: boolean;
          canCancel?: boolean;
          checkoutUrl?: string | null;
        } | null;
      }>(`/payments/pending${semester ? `?semester=${semester}` : ""}`, {}, token),

    getPendingManual: (token: string) =>
      request<{
        hasPending: boolean;
        payment: {
          id: string;
          txnId: string;
          plan: string;
          amount: number;
          date: string | null;
          status: string;
        } | null;
      }>("/payments/pending-manual", {}, token),

    cancelManual: (token: string, txRef?: string) =>
      request<{
        ok: boolean;
        status: string;
        txRef?: string;
        message: string;
      }>("/payments/manual/cancel", {
        method: "POST",
        body: JSON.stringify({ tx_ref: txRef }),
      }, token),

    cancelPayment: (token: string, txRef?: string, reason?: string) =>
      request<{
        ok: boolean;
        status: string;
        txRef?: string;
        message: string;
      }>("/payments/manual/cancel", {
        method: "POST",
        body: JSON.stringify({ tx_ref: txRef, reason }),
      }, token),
  },

  generate: {
    quiz: (
      token: string,
      body: {
        subject: string;
        difficulty: string;
        questionCount: number;
        examClass?: "final" | "mid" | "coc";
        courseId?: string;
        moduleId?: string;
        chapterId?: string;
        topicId?: string;
        topic?: string;
        regenerate?: boolean;
      },
    ) =>
      request<{
        status: string;
        title: string;
        difficulty: string;
        questionCount: number;
        questions: import("./quizSession").QuizQuestion[];
        message?: string;
        courseId?: string;
        moduleId?: string;
        chapterId?: string;
        topicId?: string;
        chapterTitle?: string;
        topicTitle?: string;
        classificationStatus?: string;
      }>("/generate/quiz", { method: "POST", body: JSON.stringify(body) }, token),

    notes: (
      token: string,
      body: {
        topic: string;
        detailLevel?: string;
        moduleId?: string;
        subject?: string;
        courseId?: string;
        chapterId?: string;
        topicId?: string;
        regenerate?: boolean;
      },
    ) =>
      request<{
        status: string;
        topic?: string;
        content?: string;
        message?: string;
        sourceCount?: number;
        cached?: boolean;
        cacheHits?: number;
        moduleId?: string | null;
        courseId?: string | null;
        chapterId?: string | null;
        topicId?: string | null;
        chapterTitle?: string | null;
        topicTitle?: string | null;
        sourceReferences?: string[];
        classificationStatus?: string;
      }>("/generate/notes", { method: "POST", body: JSON.stringify(body) }, token),

    practiceExams: (token: string) =>
      request<{
        items: Array<{
          id: string;
          title: string;
          year?: string | null;
          examClass?: string | null;
          questionCount: number;
          course?: string | null;
        }>;
      }>("/generate/practice-exams", {}, token),

    practiceExam: (token: string, examId?: string) =>
      request<{
        status: string;
        examId?: string;
        title: string;
        questionCount: number;
        questions: import("./quizSession").QuizQuestion[];
        message?: string;
      }>("/generate/practice-exam", {
        method: "POST",
        body: JSON.stringify({ examId }),
      }, token),

    retry: (
      token: string,
      body: { subject: string; missedQuestions: string[]; questionCount?: number },
    ) =>
      request<{
        status: string;
        title: string;
        questionCount: number;
        questions: import("./quizSession").QuizQuestion[];
        message?: string;
      }>("/generate/retry", { method: "POST", body: JSON.stringify(body) }, token),

    reportQuestion: (token: string, questionId: string, reason: string) =>
      request<{
        success: boolean;
        reportId: string;
        questionId: string;
        newStatus: string;
        message: string;
      }>(`/generate/quiz/questions/${encodeURIComponent(questionId)}/report`, {
        method: "POST",
        body: JSON.stringify({ reason }),
      }, token),

    recordQuizOutcomes: (
      token: string,
      outcomes: Array<{ questionId: string; isCorrect: boolean }>,
    ) =>
      request<{
        success: boolean;
        results: Array<{
          success: boolean;
          questionId: string;
          attempts: number;
          wrongCount: number;
          wrongRate: number;
          status: string;
          autoFlagged: boolean;
        }>;
      }>("/generate/quiz/outcomes", {
        method: "POST",
        body: JSON.stringify({ outcomes }),
      }, token),

    submitQuiz: (
      token: string,
      body: {
        attemptId?: string;
        subject: string;
        courseId?: string;
        moduleId?: string;
        chapterId?: string;
        topicId?: string;
        title?: string;
        difficulty?: string;
        totalQuestions: number;
        answers: number[];
        questions: any[];
      },
    ) =>
      request<{
        attemptId: string;
        scorePercentage: number;
        totalQuestions: number;
        answeredCount: number;
        correctCount: number;
        wrongCount: number;
        passed: boolean;
        status: string;
        completedAt: string;
      }>("/generate/quiz/submit", {
        method: "POST",
        body: JSON.stringify(body),
      }, token),

    submitPracticeExam: (
      token: string,
      body: {
        examId: string;
        subject?: string;
        title?: string;
        totalQuestions: number;
        answers: number[];
        questions: any[];
      },
    ) =>
      request<{
        attemptId: string;
        scorePercentage: number;
        totalQuestions: number;
        answeredCount: number;
        correctCount: number;
        wrongCount: number;
        passed: boolean;
        status: string;
        completedAt: string;
      }>("/generate/practice-exam/submit", {
        method: "POST",
        body: JSON.stringify(body),
      }, token),
  },

  student: {
    /** A chapter figure image (private; needs the student's login and notes access). */
    chapterAsset: (token: string, chapterId: string, src: string, version?: number) =>
      requestBlob(
        `/study/chapters/${encodeURIComponent(chapterId)}/assets/${src.replace(/^assets\//, "")}${version ? `?version=${version}` : ""}`,
        token,
      ),
    chapters: chaptersStudentApi(request),
    dashboard: (token: string, previewUserId?: string) =>
      request<{
        greeting: string;
        subtitle: string;
        stats: { label: string; value: string; sub: string }[];
        activityStats?: {
          quizzesCompleted: number;
          quizzesStarted: number;
          examsCompleted: number;
          cocCompleted: number;
        };
        continueStudying: StudentResource | null;
        recentResources: StudentResource[];
        recommendedModules: StudentResource[];
        upcomingExams: { subject: string; title?: string; date: string; daysLeft: number; examId?: string }[];
        isPremium: boolean;
        resourceCount?: number;
      }>(`/student/dashboard${previewUserId ? `?preview_user_id=${encodeURIComponent(previewUserId)}` : ""}`, {}, token),

    activityStats: (token: string) =>
      request<{
        quizzesCompleted: number;
        quizzesStarted: number;
        examsCompleted: number;
        cocCompleted: number;
      }>("/student/activity-stats", {}, token),

    modules: {
      list: (
        token: string,
        params: {
          subject?: string;
          courseId?: string;
          search?: string;
          resourceType?: "module" | "teacher_resource";
        } = {},
      ) => {
        const qs = new URLSearchParams();
        if (params.subject && params.subject !== "All") qs.set("subject", params.subject);
        if (params.courseId) qs.set("courseId", params.courseId);
        if (params.search) qs.set("search", params.search);
        if (params.resourceType) qs.set("resourceType", params.resourceType);
        const q = qs.toString();
        return request<{ items: StudentResource[]; tabs: string[]; subjects?: StudySubjectItem[] }>(
          `/modules${q ? `?${q}` : ""}`,
          {},
          token,
        );
      },
      downloadBlob: (token: string, resourceId: string) =>
        requestBlob(`/modules/${resourceId}/download?download=true`, token),
      /** Inline file for the online viewer (the server enforces access on this request). */
      viewBlob: (token: string, resourceId: string) => requestBlob(`/modules/${resourceId}/download`, token),
      /** URL and headers for PDF.js, which fetches the file in ranges so page 1 shows before the whole file arrives. */
      viewSource: async (token: string, resourceId: string) => ({
        url: `${API_BASE}/modules/${encodeURIComponent(resourceId)}/download`,
        headers: { Authorization: `Bearer ${await ensureValidToken(token)}` } as Record<string, string>,
      }),
      card: (token: string, resourceId: string) =>
        request<{ id: string; title: string; locked?: boolean; pages?: number }>(`/modules/${resourceId}`, {}, token),
      curriculum: (token: string, moduleId: string) =>
        request<ModuleCurriculum>(`/modules/${moduleId}/curriculum`, {}, token),
      curriculumBySubject: (token: string, subject: string, courseId?: string) => {
        const params = new URLSearchParams({ subject });
        if (courseId) params.append("courseId", courseId);
        return request<{ subject: string; modules: ModuleCurriculum[] }>(
          `/modules/curriculum?${params.toString()}`,
          {},
          token,
        );
      },
      chapterContent: (token: string, moduleId: string, chapterId: string, topicId?: string) =>
        request<{
          chapterId: string;
          chapterTitle: string;
          topicId: string | null;
          topicTitle: string;
          markdown: string;
          figures: ContentFigure[];
          version: number;
          source: string;
          pages: number[];
          topics: Array<{ id: string; title: string; sortOrder?: number }>;
          topicContents: Array<{
            id: string;
            topicId: string | null;
            topicTitle: string;
            markdown: string;
            figures: ContentFigure[];
            version: number;
            source: string;
            pages: number[];
          }>;
        }>(`/modules/${moduleId}/chapters/${chapterId}/content${topicId ? `?topicId=${topicId}` : ""}`, {}, token),
      shortNote: (token: string, topicId: string) =>
        request<TopicNoteReaderResponse>(`/modules/notes/${topicId}`, {}, token),
    },

    subjects: {
      list: (token: string) =>
        request<{ items: StudySubjectItem[] }>("/modules/subjects", {}, token).then((res) => ({
          ...res,
          items: (res.items || []).map((item) => ({
            ...item,
            imageUrl: resolveImageUrl(item.imageUrl),
          })),
        })),
      get: (token: string, slug: string) =>
        request<StudySubjectItem>(`/modules/subjects/${encodeURIComponent(slug)}`, {}, token).then((item) => ({
          ...item,
          imageUrl: resolveImageUrl(item.imageUrl),
        })),
    },

    exams: {
      list: (
        token: string,
        params: { subject?: string; courseId?: string; examClass?: "final" | "mid" | "coc" } = {},
      ) => {
        const qs = new URLSearchParams();
        if (params.subject && params.subject !== "All") qs.set("subject", params.subject);
        if (params.courseId) qs.set("courseId", params.courseId);
        if (params.examClass) qs.set("examClass", params.examClass);
        const q = qs.toString();
        return request<StudentExam[]>(`/exams${q ? `?${q}` : ""}`, {}, token);
      },

      get: (token: string, examId: string) =>
        request<{
          id: string;
          title: string;
          year: string;
          subject?: string;
          questionCount?: number;
          examClass?: string | null;
          locked: boolean;
        }>(`/exams/${examId}`, {}, token),

      content: (token: string, examId: string) =>
        request<{
          questions?: Array<{
            number?: number;
            text?: string;
            options?: Array<{ label: string; text: string }>;
            answer?: { label?: string | null; text?: string; explanation?: string };
          }>;
          metadata?: {
            partial_extraction?: boolean;
            extraction_warnings?: string[];
            [key: string]: unknown;
          };
        }>(`/exams/${examId}/content`, {}, token),
    },

    coc: {
      list: (token: string) => request<CocExam[]>("/coc", {}, token),
      categories: (token: string) => request<string[]>("/coc/categories", {}, token),
      get: (token: string, cocId: string) => request<CocExam>(`/coc/${cocId}`, {}, token),
      submit: (
        token: string,
        cocId: string,
        answers: Record<string, number>,
        timeSpentSeconds?: number,
      ) =>
        request<CocScoreResponse>(
          `/coc/${cocId}/submit`,
          {
            method: "POST",
            body: JSON.stringify({ answers, time_spent_seconds: timeSpentSeconds }),
          },
          token,
        ),
      score: (
        token: string,
        cocId: string,
        answers: Record<string, number>,
        timeSpentSeconds?: number,
      ) =>
        request<CocScoreResponse>(
          `/coc/${cocId}/score`,
          {
            method: "POST",
            body: JSON.stringify({ answers, time_spent_seconds: timeSpentSeconds }),
          },
          token,
        ),
      explanations: (token: string, cocId: string) =>
        request<{
          exam_id: string;
          access: string;
          is_preview: boolean;
          explanations: Array<{ id: string; correct_answer: number; explanation?: string }>;
        }>(`/coc/${cocId}/explanations`, {}, token),
      downloadBlob: (token: string, cocId: string) =>
        requestBlob(`/coc/${cocId}/download`, token),
    },
  },

  admin: {
    chapters: chaptersAdminApi(request),
    ingestReview: ingestReviewApi(request),
    coc: {
      list: (token: string) => request<CocExam[]>("/admin/coc", {}, token),
      get: (token: string, cocId: string) => request<CocExam>(`/admin/coc/${cocId}`, {}, token),
      create: (token: string, body: CocExamCreate) =>
        request<CocExam>("/admin/coc", { method: "POST", body: JSON.stringify(body) }, token),
      update: (token: string, cocId: string, body: Partial<CocExamCreate>) =>
        request<CocExam>(`/admin/coc/${cocId}`, { method: "PUT", body: JSON.stringify(body) }, token),
      delete: (token: string, cocId: string) =>
        request<{ message: string }>(`/admin/coc/${cocId}`, { method: "DELETE" }, token),
      bulk: (
        token: string,
        body: { exam_ids: string[]; action: "delete" | "publish" | "unpublish" },
      ) =>
        request<{ message: string; count: number }>(
          "/admin/coc/bulk",
          { method: "POST", body: JSON.stringify(body) },
          token,
        ),
    },

    overview: (token: string) =>
      request<{
        stats: AdminStat[];
        regChart: ChartPoint[];
        recentPayments: AdminPayment[];
        activity: AdminActivity[];
      }>("/admin/overview", {}, token),

    users: (token: string, params: { search?: string; plan?: string; status?: string } = {}) => {
      const qs = new URLSearchParams();
      if (params.search) qs.set("search", params.search);
      if (params.plan && params.plan !== "all") qs.set("plan", params.plan);
      if (params.status && params.status !== "all") qs.set("status", params.status);
      const q = qs.toString();
      return request<{ items: AdminUser[]; total: number; counts: AdminListCounts }>(
        `/admin/users${q ? `?${q}` : ""}`,
        {},
        token,
      );
    },

    updateUser: (
      token: string,
      userId: string,
      body: {
        is_active?: boolean;
        is_premium?: boolean;
        full_name?: string;
        phone?: string;
        university_id?: string | null;
        department?: string | null;
        stream_id?: string | null;
        semester?: number;
        preferred_language?: "en" | "am" | "oro";
        premium_months?: number;
        premium_days?: number;
      },
    ) => request<AdminUser>(`/admin/users/${userId}`, { method: "PATCH", body: JSON.stringify(body) }, token),

    deleteUser: (token: string, userId: string) =>
      request<void>(`/admin/users/${userId}`, { method: "DELETE" }, token),

    bulkUsersAction: (
      token: string,
      body: {
        action: "deactivate" | "activate" | "grant_premium" | "revoke_premium" | "delete";
        user_ids: string[];
        premium_months?: number;
        premium_days?: number;
      },
    ) => request<{ ok: boolean; count: number; message: string }>("/admin/users/bulk-action", {
      method: "POST",
      body: JSON.stringify(body),
    }, token),

    bulkResourcesAction: (
      token: string,
      body: {
        action: "delete" | "make_free" | "make_premium" | "reingest" | "publish" | "unpublish";
        resource_ids: string[];
        exam_class?: string;
      },
    ) => request<{ ok: boolean; count: number; message: string }>("/admin/resources/bulk-action", {
      method: "POST",
      body: JSON.stringify(body),
    }, token),

    bulkUniversitiesAction: (
      token: string,
      body: {
        action: "activate" | "deactivate" | "delete";
        university_ids: string[];
      },
    ) =>
      request<{ message: string; count: number }>("/admin/catalog/universities/bulk", {
        method: "POST",
        body: JSON.stringify(body),
      }, token),

    bulkCoursesAction: (
      token: string,
      body: {
        action: "delete" | "set_semester";
        course_ids: string[];
        semester?: number | null;
      },
    ) =>
      request<{ message: string; count: number; ok?: boolean; skippedCount?: number }>("/admin/catalog/courses/bulk", {
        method: "POST",
        body: JSON.stringify(body),
      }, token),

    packages: {
      list: (token: string) => request<{ items: AdminPremiumPackage[] }>("/admin/packages", {}, token),
      create: (token: string, body: AdminPackageCreate) =>
        request<{ ok: boolean; package: AdminPremiumPackage; message: string }>("/admin/packages", { method: "POST", body: JSON.stringify(body) }, token),
      update: (token: string, id: string, body: Partial<AdminPackageCreate>) =>
        request<{ ok: boolean; package: AdminPremiumPackage; message: string }>(`/admin/packages/${id}`, { method: "PATCH", body: JSON.stringify(body) }, token),
      delete: (token: string, id: string) =>
        request<{ ok: boolean; message: string }>(`/admin/packages/${id}`, { method: "DELETE" }, token),
      reorder: (token: string, items: { id: string; display_order: number }[]) =>
        request<{ ok: boolean; message: string }>("/admin/packages/reorder", { method: "POST", body: JSON.stringify({ items }) }, token),
    },

    entitlements: {
      list: (token: string, params: { search?: string; userId?: string; status?: string; stream?: string; semester?: number } = {}) => {
        const qs = new URLSearchParams();
        if (params.search) qs.set("search", params.search);
        if (params.userId) qs.set("user_id", params.userId);
        if (params.status && params.status !== "all") qs.set("status", params.status);
        if (params.stream && params.stream !== "all") qs.set("stream", params.stream);
        if (params.semester) qs.set("semester", String(params.semester));
        const q = qs.toString();
        return request<{ items: AdminEntitlementItem[]; total: number }>(`/admin/entitlements${q ? `?${q}` : ""}`, {}, token);
      },
      grant: (token: string, body: { user_id: string; stream: string; semester: number; duration_months: number; package_id?: string; reason?: string }) =>
        request<{ ok: boolean; message: string; entitlement: AdminEntitlementItem }>("/admin/entitlements/grant", { method: "POST", body: JSON.stringify(body) }, token),
      revoke: (token: string, entitlementId: string, body?: { reason?: string }) =>
        request<{ ok: boolean; message: string }>(`/admin/entitlements/${entitlementId}/revoke`, { method: "POST", body: JSON.stringify(body || {}) }, token),
    },

    me: (token: string) =>
      request<{
        id: string;
        fullName: string;
        phone: string;
        isSuperAdmin: boolean;
        permissions: string[];
        featureCatalog: { key: string; label: string }[];
        defaultPermissions: string[];
      }>("/admin/me", {}, token),

    updateMe: (
      token: string,
      body: {
        full_name?: string;
        current_password?: string;
        new_password?: string;
      },
    ) =>
      request<{
        id: string;
        fullName: string;
        phone: string;
        isSuperAdmin: boolean;
        permissions: string[];
        featureCatalog: { key: string; label: string }[];
        defaultPermissions: string[];
      }>("/admin/me", {
        method: "PATCH",
        body: JSON.stringify(body),
      }, token),

    catalogUniversities: (token: string, search?: string) => {
      const q = search ? `?search=${encodeURIComponent(search)}` : "";
      return request<{ items: CatalogUniversity[] }>(`/admin/catalog/universities${q}`, {}, token);
    },

    catalogCourses: (
      token: string,
      params?: { search?: string; streamId?: string; semester?: string } | string,
    ) => {
      const qs = new URLSearchParams();
      if (typeof params === "string") {
        if (params.trim()) qs.set("search", params.trim());
      } else if (params) {
        if (params.search?.trim()) qs.set("search", params.search.trim());
        if (params.streamId && params.streamId !== "all") qs.set("streamId", params.streamId);
        if (params.semester && params.semester !== "all") qs.set("semester", params.semester);
      }
      const q = qs.toString();
      return request<{ items: CatalogCourse[] }>(`/admin/catalog/courses${q ? `?${q}` : ""}`, {}, token);
    },

    getCourseSemesterImpact: (
      token: string,
      courseId: string,
      newSemester?: number | null,
    ) => {
      const q = newSemester !== undefined && newSemester !== null ? `?new_semester=${newSemester}` : "";
      return request<{
        courseId: string;
        courseName: string;
        currentSemester: number | null;
        newSemester: number | null;
        resourceCount: number;
        affectedStudentsCount: number;
        hasImpact: boolean;
      }>(`/admin/courses/${encodeURIComponent(courseId)}/semester-impact${q}`, {}, token);
    },

    catalogStreams: (token: string, search?: string) => {
      const q = search ? `?search=${encodeURIComponent(search)}` : "";
      return request<{ items: CatalogStream[] }>(`/admin/catalog/streams${q}`, {}, token);
    },

    createUniversity: (
      token: string,
      body: { name: string; short_name: string; region?: string },
    ) => request<CatalogUniversity>("/admin/universities", { method: "POST", body: JSON.stringify(body) }, token),

    createUniversitiesBatch: (
      token: string,
      items: { name: string; short_name: string; region?: string }[],
    ) => request<{ ok: boolean; created_count: number; skipped_count: number; items: CatalogUniversity[]; message: string }>(
      "/admin/universities/batch",
      { method: "POST", body: JSON.stringify({ items }) },
      token,
    ),

    createCourse: (
      token: string,
      body: {
        name: string;
        code: string;
        description?: string;
        stream_id?: string;
        semester?: number | null;
        image_url?: string | null;
        course_type?: string;
        extraction_profile?: string | null;
      },
    ) => request<CatalogCourse>("/admin/courses", { method: "POST", body: JSON.stringify(body) }, token),

    createCoursesBatch: (
      token: string,
      items: {
        name: string;
        code: string;
        description?: string;
        stream_id?: string;
        semester?: number | null;
        image_url?: string | null;
        course_type?: string;
        extraction_profile?: string | null;
      }[],
    ) => request<{ ok: boolean; created_count: number; skipped_count: number; items: CatalogCourse[]; message: string }>(
      "/admin/courses/batch",
      { method: "POST", body: JSON.stringify({ items }) },
      token,
    ),

    createStream: (
      token: string,
      body: { name: string; code?: string; description?: string; is_active?: boolean },
    ) => request<CatalogStream>("/admin/streams", { method: "POST", body: JSON.stringify(body) }, token),

    createStreamsBatch: (
      token: string,
      items: { name: string; code?: string; description?: string }[],
    ) => request<{ ok: boolean; created_count: number; skipped_count: number; items: CatalogStream[]; message: string }>(
      "/admin/streams/batch",
      { method: "POST", body: JSON.stringify({ items }) },
      token,
    ),

    updateStream: (
      token: string,
      id: string,
      body: { name?: string; code?: string; description?: string | null; is_active?: boolean },
    ) => request<CatalogStream>(`/admin/streams/${id}`, { method: "PATCH", body: JSON.stringify(body) }, token),

    deleteStream: (token: string, id: string) =>
      request<void>(`/admin/streams/${id}`, { method: "DELETE" }, token),

    updateUniversity: (
      token: string,
      id: string,
      body: { name?: string; short_name?: string; region?: string | null; is_active?: boolean },
    ) => request<CatalogUniversity>(`/admin/universities/${id}`, { method: "PATCH", body: JSON.stringify(body) }, token),

    deleteUniversity: (token: string, id: string) =>
      request<void>(`/admin/universities/${id}`, { method: "DELETE" }, token),

    updateCourse: (
      token: string,
      id: string,
      body: {
        name?: string;
        code?: string;
        description?: string | null;
        stream_id?: string | null;
        semester?: number | null;
        image_url?: string | null;
        is_premium?: boolean;
        course_type?: string | null;
        extraction_profile?: string | null;
      },
    ) => request<CatalogCourse>(`/admin/courses/${id}`, { method: "PATCH", body: JSON.stringify(body) }, token),

    uploadCourseImage: async (token: string, file: File, courseId?: string) => {
      let authToken: string;
      try {
        authToken = await ensureValidToken(token);
      } catch (err) {
        throw err instanceof ApiError ? err : new ApiError(401, "Session expired");
      }

      const form = new FormData();
      form.append("file", file);

      const endpoint = courseId
        ? `${API_BASE}/admin/courses/${courseId}/image`
        : `${API_BASE}/admin/courses/upload-image`;

      const doUpload = async (bearer: string, retried = false): Promise<Response> => {
        const res = await fetch(endpoint, {
          method: "POST",
          headers: { Authorization: `Bearer ${bearer}` },
          body: form,
        });
        if (res.status === 401 && !retried && authBridge) {
          const refreshed = await authBridge.refreshSession();
          if (refreshed) return doUpload(refreshed.accessToken, true);
          authBridge.logout();
        }
        return res;
      };

      const res = await doUpload(authToken);
      if (!res.ok) {
        let message = res.statusText;
        try {
          const body = await res.json();
          if (typeof body.message === "string") message = body.message;
          else if (typeof body.detail === "string") message = body.detail;
        } catch { /* ignore */ }
        throw new ApiError(res.status, String(message || "Image upload failed"));
      }
      return (await res.json()) as { imageUrl: string; course?: CatalogCourse };
    },

    deleteCourse: (token: string, id: string) =>
      request<void>(`/admin/courses/${id}`, { method: "DELETE" }, token),

    listAdmins: (token: string) =>
      request<{
        items: AdminAccount[];
        featureCatalog: { key: string; label: string }[];
        defaultPermissions: string[];
      }>("/admin/admins", {}, token),

    createAdmin: (
      token: string,
      body: {
        phone: string;
        full_name: string;
        password: string;
        is_super_admin?: boolean;
        permissions?: string[];
        telegram_user_id?: number;
      },
    ) => request<AdminAccount>("/admin/admins", { method: "POST", body: JSON.stringify(body) }, token),

    updateAdmin: (
      token: string,
      id: string,
      body: {
        full_name?: string;
        is_active?: boolean;
        is_super_admin?: boolean;
        permissions?: string[];
        password?: string;
      },
    ) => request<AdminAccount>(`/admin/admins/${id}`, { method: "PATCH", body: JSON.stringify(body) }, token),

    deleteAdmin: (token: string, id: string) =>
      request<void>(`/admin/admins/${id}`, { method: "DELETE" }, token),

    promoteUser: (
      token: string,
      userId: string,
      body: { permissions?: string[]; is_super_admin?: boolean; custom_message?: string; password?: string } = {},
    ) =>
      request<{
        ok: boolean;
        message: string;
        user: AdminAccount;
        pendingSetup: boolean;
      }>(`/admin/users/${userId}/promote`, {
        method: "POST",
        body: JSON.stringify(body),
      }, token),

    revokeAdmin: (
      token: string,
      userId: string,
      body: { reason?: string } = {},
    ) =>
      request<{
        ok: boolean;
        message: string;
        user: AdminUser;
      }>(`/admin/users/${userId}/revoke`, {
        method: "POST",
        body: JSON.stringify(body),
      }, token),

    listAuditLogs: (
      token: string,
      params: { limit?: number; action?: string } = {},
    ) => {
      const qs = new URLSearchParams();
      if (params.limit) qs.set("limit", String(params.limit));
      if (params.action) qs.set("action", params.action);
      const q = qs.toString();
      return request<{
        items: Array<{
          id: string;
          actorId: string | null;
          actorName: string;
          targetId: string;
          targetName: string;
          targetPhone: string;
          action: string;
          previousRole: string;
          newRole: string;
          status: string;
          details?: unknown;
          createdAt: string;
        }>;
        total: number;
      }>(`/admin/audit-logs${q ? `?${q}` : ""}`, {}, token);
    },

    resources: (token: string, params: { search?: string; type?: string; status?: string; access?: string; stream?: string } = {}) => {
      const qs = new URLSearchParams();
      if (params.search) qs.set("search", params.search);
      if (params.type && params.type !== "all") qs.set("type", params.type);
      if (params.status && params.status !== "all") qs.set("status", params.status);
      if (params.access && params.access !== "all") qs.set("access", params.access);
      if (params.stream && params.stream !== "all") qs.set("stream", params.stream);
      const q = qs.toString();
      return request<{
        items: AdminResource[];
        total: number;
        counts: AdminListCounts;
        uploadVia: string;
        botUsername: string | null;
      }>(`/admin/resources${q ? `?${q}` : ""}`, {}, token);
    },

    deleteResource: (token: string, resourceId: string) =>
      request<void>(`/admin/resources/${resourceId}`, { method: "DELETE" }, token),

    uploadResource: async (
      token: string,
      form: FormData,
    ) => {
      let authToken: string;
      try {
        authToken = await ensureValidToken(token);
      } catch (err) {
        throw err instanceof ApiError ? err : new ApiError(401, "Session expired");
      }

      const doUpload = async (bearer: string, retried = false) => {
        const res = await fetch(`${API_BASE}/admin/resources/upload`, {
          method: "POST",
          headers: { Authorization: `Bearer ${bearer}` },
          body: form,
        });
        if (res.status === 401 && !retried && authBridge) {
          const refreshed = await authBridge.refreshSession();
          if (refreshed) return doUpload(refreshed.accessToken, true);
          authBridge.logout();
        }
        return res;
      };

      const res = await doUpload(authToken);
      if (!res.ok) {
        let message = res.statusText;
        try {
          const body = await res.json();
          if (typeof body.message === "string") message = body.message;
          else if (typeof body.detail === "string") message = body.detail;
          else if (Array.isArray(body.detail)) {
            message = body.detail.map((e: { msg?: string }) => e.msg ?? "").join(", ");
          }
        } catch { /* ignore */ }
        throw new ApiError(res.status, String(message));
      }
      const data = await res.json();
      const id = data.id != null ? String(data.id) : "";
      if (!id) throw new ApiError(500, "Upload succeeded but server returned no resource id");
      return {
        id,
        title: data.title ?? "",
        ingestionStatus: data.ingestionStatus ?? data.ingestion_status ?? "pending",
      };
    },

    uploadExamBatch: async (token: string, form: FormData) => {
      let authToken: string;
      try {
        authToken = await ensureValidToken(token);
      } catch (err) {
        throw err instanceof ApiError ? err : new ApiError(401, "Session expired");
      }

      const doUpload = async (bearer: string, retried = false) => {
        const res = await fetch(`${API_BASE}/admin/resources/upload-batch`, {
          method: "POST",
          headers: { Authorization: `Bearer ${bearer}` },
          body: form,
        });
        if (res.status === 401 && !retried && authBridge) {
          const refreshed = await authBridge.refreshSession();
          if (refreshed) return doUpload(refreshed.accessToken, true);
          authBridge.logout();
        }
        return res;
      };

      const res = await doUpload(authToken);
      if (!res.ok) {
        let message = res.statusText;
        try {
          const body = await res.json();
          if (typeof body.message === "string") message = body.message;
          else if (typeof body.detail === "string") message = body.detail;
          else if (Array.isArray(body.detail)) {
            message = body.detail.map((e: { msg?: string }) => e.msg ?? "").join(", ");
          }
        } catch { /* ignore */ }
        throw new ApiError(res.status, String(message));
      }
      const data = await res.json();
      const id = data.id != null ? String(data.id) : "";
      if (!id) throw new ApiError(500, "Upload succeeded but server returned no resource id");
      return {
        id,
        title: data.title ?? "",
        ingestionStatus: data.ingestionStatus ?? data.ingestion_status ?? "pending",
        fileCount: data.fileCount ?? data.file_count ?? 0,
      };
    },

    getResource: (token: string, resourceId: string) =>
      request<{
        id: string;
        title: string;
        ingestionStatus: string;
        ingestionError?: string | null;
        questionCount?: number | null;
        pageCount?: number | null;
        canReingest?: boolean;
        canStop?: boolean;
        canContinue?: boolean;
        courseId?: string | null;
        universityId?: string | null;
        isFree?: boolean;
        examYear?: string | null;
        isPublished?: boolean;
        examClass?: "final" | "mid" | "coc" | null;
        accessStats?: {
          views: number;
          downloads: number;
          practices: number;
          uniqueViewers: number;
          uniquePractitioners: number;
          views7d: number;
          lastAccessAt?: string | null;
        };
        recentAccess?: Array<{ userId: string; userName: string; action: string; at: string }>;
      }>(`/admin/resources/${resourceId}`, {}, token),

    getResourceContent: (token: string, resourceId: string) =>
      request<{
        id: string;
        title: string;
        resourceType: string;
        ingestionStatus: string;
        ingestionError?: string | null;
        pageCount?: number | null;
        questionCount?: number | null;
        plainText?: string | null;
        extractedData?: Record<string, unknown> | null;
        questions?: Array<{
          number?: number;
          text?: string;
          question_type?: string;
          options?: Array<{ label: string; text: string }>;
          answer?: { label?: string | null; text?: string; explanation?: string; confidence?: string; source?: string };
        }>;
        analysis?: Record<string, unknown> | null;
        hasTelegramFile?: boolean;
        mimeType?: string | null;
        isPublished?: boolean;
        examClass?: "final" | "mid" | "coc" | null;
        publishedAt?: string | null;
        extractionFiles?: Array<{
          file_name?: string;
          status?: string;
          page_count?: number;
          question_count?: number;
          error?: string;
          failed?: boolean;
        }> | null;
        canReingest?: boolean;
        canStop?: boolean;
        canContinue?: boolean;
        courseId?: string | null;
        universityId?: string | null;
        isFree?: boolean;
        examYear?: string | null;
      }>(`/admin/resources/${resourceId}/content`, {}, token),

    getCurriculumSplit: (token: string, resourceId: string) =>
      request<CurriculumSplitResponse>(`/admin/resources/${resourceId}/curriculum`, {}, token),

    confirmCurriculumSplit: (
      token: string,
      resourceId: string,
      chapters: Array<{
        id?: string;
        title: string;
        sort_order?: number;
        start_page?: number | null;
        end_page?: number | null;
      }>
    ) =>
      request<ConfirmSplitResponse>(
        `/admin/resources/${resourceId}/confirm-split`,
        {
          method: "POST",
          body: JSON.stringify({ chapters }),
        },
        token
      ),

    exportContentPacket: (token: string, resourceId: string) =>
      request<ContentPacketResponse>(
        `/admin/resources/${resourceId}/export-content`,
        {},
        token
      ),

    exportManualPacket: (
      token: string,
      resourceId: string,
      chapterId: string,
      body: {
        topicId?: string | null;
        questionCount?: number;
        easyPct?: number;
        mediumPct?: number;
        hardPct?: number;
        language?: string;
      } = {}
    ) =>
      request<ManualExportPacketResponse>(
        `/admin/resources/${resourceId}/chapters/${chapterId}/manual-packet`,
        {
          method: "POST",
          body: JSON.stringify(body),
        },
        token
      ),

    reingestResource: (token: string, resourceId: string) =>
      request<{ id: string; ingestionStatus?: string; ingestion_status?: string; message: string }>(
        `/admin/resources/${resourceId}/reingest`,
        { method: "POST" },
        token,
      ),

    stopIngestion: (token: string, resourceId: string) =>
      request<{ id: string; ingestionStatus: string; message: string }>(
        `/admin/resources/${resourceId}/stop`,
        { method: "POST" },
        token,
      ),

    continueIngestion: (token: string, resourceId: string) =>
      request<{ id: string; ingestionStatus: string; message: string }>(
        `/admin/resources/${resourceId}/continue`,
        { method: "POST" },
        token,
      ),

    updateResource: (
      token: string,
      resourceId: string,
      body: {
        title?: string;
        courseId?: string;
        universityId?: string;
        streamId?: string | null;
        isFree?: boolean;
        examYear?: string;
      },
    ) =>
      request<{
        id: string;
        title: string;
        ingestionStatus: string;
        courseId?: string | null;
        universityId?: string | null;
        streamId?: string | null;
        isFree?: boolean;
        examYear?: string | null;
        message: string;
      }>(
        `/admin/resources/${resourceId}`,
        {
          method: "PATCH",
          body: JSON.stringify({
            ...(body.title !== undefined ? { title: body.title } : {}),
            ...(body.courseId !== undefined ? { course_id: body.courseId } : {}),
            ...(body.universityId !== undefined ? { university_id: body.universityId } : {}),
            ...(body.streamId !== undefined ? { stream_id: body.streamId } : {}),
            ...(body.isFree !== undefined ? { is_free: body.isFree } : {}),
            ...(body.examYear !== undefined ? { exam_year: body.examYear } : {}),
          }),
        },
        token,
      ),

    publishExam: (
      token: string,
      resourceId: string,
      body: { examClass: "final" | "mid" | "coc"; notifyStudents?: boolean },
    ) =>
      request<{
        id: string;
        message: string;
        examClass: string;
        isPublished: boolean;
        publishedAt: string | null;
        notifiedCount?: number;
        failedCount?: number;
      }>(
        `/admin/resources/${resourceId}/publish`,
        {
          method: "POST",
          body: JSON.stringify({
            exam_class: body.examClass,
            notify_students: body.notifyStudents ?? false,
          }),
        },
        token,
      ),

    unpublishExam: (token: string, resourceId: string) =>
      request<{ id: string; message: string; isPublished: boolean }>(
        `/admin/resources/${resourceId}/unpublish`,
        { method: "POST" },
        token,
      ),

    extractModule: (token: string, moduleId: string) =>
      request<{ ok: boolean; message: string; extractionStatus: string }>(
        `/admin/modules/${moduleId}/extract`,
        { method: "POST" },
        token,
      ),

    getModuleDraft: (token: string, moduleId: string) =>
      request<{
        moduleId: string;
        moduleTitle: string;
        courseId?: string | null;
        courseName?: string | null;
        status: string;
        chapters: Array<{
          id: string;
          title: string;
          sortOrder: number;
          topics: Array<{ id: string; title: string }>;
          notes: Array<{ id: string; topic_title: string; markdown: string; key_terms: string[] }>;
          questions: Array<{
            id: string;
            question: string;
            options: string[];
            answer: number;
            explanation: string;
            difficulty?: string;
          }>;
        }>;
        rawDraft?: unknown;
        error?: string | null;
      }>(`/admin/modules/${moduleId}/draft`, {}, token),

    updateModuleDraft: (token: string, moduleId: string, body: { chapters: unknown[]; questions?: unknown[] }) =>
      request<{ ok: boolean; message: string; draft: unknown }>(
        `/admin/modules/${moduleId}/draft`,
        { method: "PUT", body: JSON.stringify(body) },
        token,
      ),

    publishModuleExtraction: (token: string, moduleId: string) =>
      request<{ ok: boolean; message: string; draft: unknown }>(
        `/admin/modules/${moduleId}/publish`,
        { method: "POST" },
        token,
      ),

    rejectModuleExtraction: (token: string, moduleId: string, reason?: string) =>
      request<{ ok: boolean; message: string; draft: unknown }>(
        `/admin/modules/${moduleId}/reject`,
        { method: "POST", body: JSON.stringify({ reason }) },
        token,
      ),

    unpublishModuleExtraction: (token: string, moduleId: string) =>
      request<{ ok: boolean; message: string; draft: unknown }>(
        `/admin/modules/${moduleId}/unpublish`,
        { method: "POST" },
        token,
      ),

    regenerateModuleExtraction: (token: string, moduleId: string) =>
      request<{ ok: boolean; message: string; extractionStatus: string }>(
        `/admin/modules/${moduleId}/regenerate`,
        { method: "POST" },
        token,
      ),

    injectQuiz: (
      token: string,
      body: {
        subject: string;
        difficulty: "Easy" | "Medium" | "Hard";
        examClass?: "final" | "mid" | "coc" | null;
        courseId?: string | null;
        moduleId?: string | null;
        chapterId?: string | null;
        topicId?: string | null;
        topic?: string | null;
        status?: "active" | "draft";
        replaceExisting?: boolean;
        questions: Array<{
          question: string;
          options: string[];
          correct: number;
          difficulty?: "Easy" | "Medium" | "Hard";
          explanation?: string;
          chapterTitle?: string;
          topicTitle?: string;
          sourceReference?: string;
        }>;
      },
    ) =>
      request<{
        status: string;
        cacheKey: string;
        questionCount: number;
        title: string;
        subject: string;
        difficulty: string;
        examClass?: string | null;
        cacheId: string;
        replaced: boolean;
      }>("/admin/quiz/manual", { method: "POST", body: JSON.stringify(body) }, token),

    unifiedImport: (
      token: string,
      body: {
        rawJson?: string;
        payload?: unknown;
        files?: Array<{ filename?: string; rawJson?: string; payload?: unknown; size?: number }>;
        courseId?: string;
        moduleId?: string;
        universityId?: string;
        dryRun?: boolean;
        status?: "draft" | "active" | "retired";
        replaceExisting?: boolean;
      },
    ) =>
      request<{
        valid: boolean;
        kind?: "content" | "notes" | "quiz" | "unknown";
        dryRun: boolean;
        status?: string;
        courseId?: string;
        moduleId?: string;
        counts?: Record<string, number>;
        batchId?: string;
        cacheId?: string;
        chapterId?: string;
        chapterTitle?: string;
        insertedIds?: string[];
        duplicates?: Array<{
          index: number;
          question: string;
          reason: string;
          similarity?: number;
          matchedWith?: string;
          hash?: string;
        }>;
        errors?: Array<{ path: string; message: string; type: string }>;
        warnings?: Array<{ message: string; type: string }>;
        wasRepaired?: boolean;
        repairChanges?: Array<{ line: number; col: number; original: string; repaired: string }>;
        results?: any[];
        totalFiles?: number;
      }>("/admin/import", { method: "POST", body: JSON.stringify(body) }, token),

    repairLatex: (token: string, rawJson: string) =>
      request<{
        wasRepaired: boolean;
        repairedJson: string;
        changes: Array<{ line: number; col: number; original: string; repaired: string }>;
        changeCount: number;
      }>("/admin/import/repair-latex", { method: "POST", body: JSON.stringify({ rawJson }) }, token),

    injectExamQuestions: (
      token: string,
      examId: string,
      body: {
        replace?: boolean;
        metadata?: Record<string, unknown>;
        sections?: Array<Record<string, unknown>>;
        sources?: Array<Record<string, unknown>>;
        questions: Array<Record<string, unknown>>;
      },
    ) =>
      request<{
        status: string;
        examId: string;
        title: string;
        questionCount: number;
        newQuestionsAdded: number;
        replaced: boolean;
        ingestionStatus: string;
        isPublished: boolean;
        note: string;
      }>(`/admin/exams/${examId}/questions`, { method: "POST", body: JSON.stringify(body) }, token),

    injectExamManual: (
      token: string,
      body: {
        title: string;
        courseId: string;
        course_id?: string;
        universityId?: string | null;
        university_id?: string | null;
        examClass?: string | null;
        exam_class?: string | null;
        year?: number | null;
        replace?: boolean;
        replaceExisting?: boolean;
        metadata?: Record<string, unknown>;
        sections?: Array<Record<string, unknown>>;
        sources?: Array<Record<string, unknown>>;
        questions: Array<Record<string, unknown>>;
      },
    ) =>
      request<{
        status: string;
        examId: string;
        title: string;
        questionCount: number;
        newQuestionsAdded: number;
        replaced: boolean;
        ingestionStatus: string;
        isPublished: boolean;
        note: string;
      }>("/admin/exams/manual", { method: "POST", body: JSON.stringify(body) }, token),


    payments: (token: string, status = "All") =>
      request<{ items: AdminPayment[] }>(
        `/admin/payments?status=${encodeURIComponent(status)}`,
        {},
        token,
      ),

    confirmPayment: (token: string, txRef: string) =>
      request<AdminPayment>(`/admin/payments/${encodeURIComponent(txRef)}/confirm`, { method: "POST" }, token),

    decidePayment: (token: string, paymentId: string, action: "approve" | "reject", reason?: string) =>
      request<{
        already_decided: boolean;
        payment_id: string;
        tx_ref: string;
        status: string;
        admin: string;
        action: string;
      }>(`/admin/payments/${encodeURIComponent(paymentId)}/decide`, {
        method: "POST",
        body: JSON.stringify({ action, reason }),
      }, token),


    analytics: (token: string) =>
      request<{
        kpis: AdminStat[];
        regChart: ChartPoint[];
        planChart: PlanSlice[];
        resourceChart: SubjectCount[];
        accessReport?: {
          totalViews: number;
          totalDownloads: number;
          totalPractices: number;
          uniqueStudents: number;
          views7d: number;
          topResources: Array<{
            id: string;
            title: string;
            resourceType: string;
            views: number;
            uniqueViewers: number;
          }>;
        };
      }>("/admin/analytics", {}, token),

    reports: (token: string) =>
      request<{
        summary: {
          totalViews: number;
          totalDownloads: number;
          totalPractices: number;
          uniqueStudents: number;
          views7d: number;
          topResources: Array<{
            id: string;
            title: string;
            resourceType: string;
            views: number;
            uniqueViewers: number;
          }>;
        };
        resources: Array<{
          id: string;
          title: string;
          subject: string;
          resourceType: string;
          ingestionStatus: string;
          isPublished: boolean;
          views: number;
          uniqueViewers: number;
          downloads: number;
          practices: number;
          lastAccessAt?: string | null;
        }>;
        stats: AdminStat[];
      }>("/admin/reports", {}, token),

    settings: (token: string) =>
      request<{
        appName: string;
        appEnv: string;
        botUsername: string | null;
        chapaConfigured: boolean;
        telebirrConfigured?: boolean;
        paymentPreferredProvider?: string;
        payment?: {
          monthlyEtb: number;
          semesterEtb: number;
          preferredProvider: string;
          anyGateway: boolean;
        };
        telegramOtpMinutes: number;
        uploadVia: string;
        isSuperAdmin?: boolean;
        permissions?: string[];
        featureCatalog?: { key: string; label: string }[];
        universities: Array<{
          id: string;
          name: string;
          short_name: string;
          region?: string | null;
          isActive?: boolean;
          resourceCount?: number;
          studentCount?: number;
        }>;
        courses: Array<{
          id: string;
          name: string;
          code: string;
          description?: string | null;
          resourceCount?: number;
        }>;
      }>("/admin/settings", {}, token),

    paymentSettings: (token: string) =>
      request<{
        monthlyEtb: number;
        semesterEtb: number;
        monthlyMonths?: number;
        semesterMonths?: number;
        semesterSavings?: string | null;
        preferredProvider: string;
        telebirrEnvironment: string;
        telebirrManualPhone?: string;
        telebirrManualName?: string;
        telebirrManualEnabled?: boolean;
        cbeAccountNumber?: string;
        cbeAccountName?: string;
        cbeReceiveLink?: string;
        cbeManualEnabled?: boolean;
        manualInstructions?: string;
        manualPaymentEnabled?: boolean;
        chapaEnabled?: boolean;
        telebirrEnabled?: boolean;
        chapa: {
          enabled?: boolean;
          configured: boolean;
          ready?: boolean;
          secretKey: { configured: boolean; source: string; hint: string | null };
          webhookSecret: { configured: boolean; source: string; hint: string | null };
        };
        telebirr: {
          enabled?: boolean;
          configured: boolean;
          ready?: boolean;
          fabricAppId: { configured: boolean; source: string; hint: string | null };
          appSecret: { configured: boolean; source: string; hint: string | null };
          merchantAppId: { configured: boolean; source: string; hint: string | null };
          merchantCode: { configured: boolean; source: string; hint: string | null };
          privateKey: { configured: boolean; source: string; hint: string | null };
          publicKey: { configured: boolean; source: string; hint: string | null };
        };
        anyGateway: boolean;
        fastestPaymentMethod?: string;
        expressCheckoutEnabled?: boolean;
        packageDiscounts?: Record<string, {
          discount_price?: number;
          original_price?: number;
          discount_badge?: string;
          is_active?: boolean;
        }>;
      }>("/admin/settings/payments", {}, token),

    updatePaymentSettings: (
      token: string,
      body: Record<string, unknown>,
    ) =>
      request<Record<string, unknown>>("/admin/settings/payments", {
        method: "PUT",
        body: JSON.stringify(body),
      }, token),

    testPaymentProvider: (token: string, provider: "chapa" | "telebirr") =>
      request<{ ok: boolean; provider: string; message: string; code: string }>(
        `/admin/settings/payments/test/${provider}`,
        { method: "POST" },
        token,
      ),

    errorLogs: (
      token: string,
      params?: {
        page?: number;
        limit?: number;
        source?: string;
        level?: string;
        user_id?: string;
        start_date?: string;
        end_date?: string;
        q?: string;
        request_id?: string;
      },
    ) => {
      const q = new URLSearchParams();
      if (params?.page) q.set("page", String(params.page));
      if (params?.limit) q.set("limit", String(params.limit));
      if (params?.source && params.source !== "all") q.set("source", params.source);
      if (params?.level && params.level !== "all") q.set("level", params.level);
      if (params?.user_id) q.set("user_id", params.user_id);
      if (params?.start_date) q.set("start_date", params.start_date);
      if (params?.end_date) q.set("end_date", params.end_date);
      if (params?.q) q.set("q", params.q);
      if (params?.request_id) q.set("request_id", params.request_id);
      return request<{
        items: Array<{
          id: string;
          createdAt: string;
          createdAtAddis: string;
          level: string;
          source: string;
          title: string;
          message: string;
          exceptionType?: string | null;
          user?: { id: string; name: string; phone?: string; role?: string } | null;
          route?: string | null;
          path?: string | null;
          method?: string | null;
          statusCode?: number | null;
          requestId?: string | null;
          fingerprint: string;
          occurrencesCount: number;
        }>;
        total: number;
        page: number;
        pageSize: number;
        totalPages: number;
      }>(`/admin/error-logs?${q.toString()}`, {}, token);
    },

    errorLogDetail: (token: string, logId: string) =>
      request<{
        id: string;
        createdAt: string;
        createdAtAddis: string;
        level: string;
        source: string;
        title: string;
        message: string;
        exceptionType?: string | null;
        stackTrace?: string | null;
        fingerprint: string;
        who: {
          id?: string | null;
          name?: string | null;
          role?: string | null;
          phone?: string | null;
          telegramId?: number | null;
          ip?: string | null;
          userAgent?: string | null;
        };
        what: {
          method?: string | null;
          path?: string | null;
          route?: string | null;
          statusCode?: number | null;
          requestId?: string | null;
          durationMs?: number | null;
          context?: Record<string, unknown> | null;
        };
        occurrences: {
          count: number;
          firstSeen: string;
          firstSeenAddis: string;
          lastSeen: string;
          lastSeenAddis: string;
          latest: Array<{
            id: string;
            createdAt: string;
            createdAtAddis: string;
            statusCode?: number | null;
            requestId?: string | null;
            path?: string | null;
          }>;
        };
      }>(`/admin/error-logs/${logId}`, {}, token),

    checkPayment: (token: string, txRef: string) =>
      request<{
        ok: boolean;
        action: string;
        message: string;
        payment: AdminPayment;
      }>(`/admin/payments/${encodeURIComponent(txRef)}/check`, { method: "POST" }, token),

    rejectPayment: (token: string, txRef: string, reason?: string) =>
      request<{
        id: string;
        user: string;
        amount: string;
        plan: string;
        date: string;
        status: string;
      }>(
        `/admin/payments/${encodeURIComponent(txRef)}/reject`,
        { method: "POST", body: JSON.stringify({ reason: reason || "Manual verification rejected" }) },
        token,
      ),

    cancelPayment: (token: string, txRef: string, reason?: string) =>
      request<{
        id: string;
        user: string;
        amount: string;
        plan: string;
        date: string;
        status: string;
      }>(
        `/admin/payments/${encodeURIComponent(txRef)}/cancel`,
        { method: "POST", body: JSON.stringify({ reason: reason || "Admin cancelled" }) },
        token,
      ),


    logs: (token: string, params: { category?: string; severity?: string; search?: string; userId?: string; limit?: number } = {}) => {
      const qs = new URLSearchParams();
      if (params.category && params.category !== "all") qs.set("category", params.category);
      if (params.severity && params.severity !== "all") qs.set("severity", params.severity);
      if (params.search) qs.set("search", params.search);
      if (params.userId) qs.set("user_id", params.userId);
      if (params.limit) qs.set("limit", String(params.limit));
      const q = qs.toString();
      return request<{ items: AdminLogEntry[]; total: number; counts: AdminListCounts }>(
        `/admin/logs${q ? `?${q}` : ""}`,
        {},
        token,
      );
    },

    userLogs: (
      token: string,
      userId: string,
      params: { category?: string; limit?: number } = {},
    ) => {
      const qs = new URLSearchParams();
      if (params.category && params.category !== "all") qs.set("category", params.category);
      if (params.limit) qs.set("limit", String(params.limit));
      const q = qs.toString();
      return request<{
        userId: string;
        userName: string;
        userPhone: string;
        plan: string;
        summary: {
          totalEvents: number;
          accessCount: number;
          errorCount: number;
          crucialCount: number;
        };
        recentAccesses: AdminLogEntry[];
        errorsFaced: AdminLogEntry[];
        crucialEvents: AdminLogEntry[];
        items: AdminLogEntry[];
      }>(`/admin/users/${userId}/logs${q ? `?${q}` : ""}`, {}, token);
    },

    passages: {
      list: (token: string, scope: string) =>
        request<Array<{
          id: string;
          title: string | null;
          text: string;
          body?: string;
          scope: string;
          linkedQuestionsCount?: number;
          createdAt?: string;
        }>>(`/admin/passages?scope=${encodeURIComponent(scope)}`, {}, token),
      create: (token: string, data: { title?: string; text: string; scope: string }) =>
        request<{
          id: string;
          title: string | null;
          text: string;
          body?: string;
          scope: string;
          linkedQuestionsCount: number;
        }>("/admin/passages", { method: "POST", body: JSON.stringify(data) }, token),
      update: (token: string, passageId: string, data: { title?: string; text: string }) =>
        request<{
          id: string;
          title: string | null;
          text: string;
          body?: string;
          scope: string;
          linkedQuestionsCount: number;
        }>(`/admin/passages/${passageId}`, { method: "PUT", body: JSON.stringify(data) }, token),
      delete: (token: string, passageId: string, scope: string) =>
        request<{
          status: string;
          deletedId: string;
          unlinkedQuestions: number;
          message: string;
        }>(`/admin/passages/${passageId}?scope=${encodeURIComponent(scope)}`, { method: "DELETE" }, token),
    },

    // Review Tools (TASK 5)
    reviewQueue: (
      token: string,
      params: {
        kind?: "all" | "quiz" | "content" | "notes";
        status?: string;
        chapterId?: string;
        topicId?: string;
        moduleId?: string;
        batchId?: string;
        page?: number;
        pageSize?: number;
      } = {}
    ) => {
      const qs = new URLSearchParams();
      if (params.kind && params.kind !== "all") qs.set("kind", params.kind);
      if (params.status) qs.set("status", params.status);
      if (params.chapterId) qs.set("chapterId", params.chapterId);
      if (params.topicId) qs.set("topicId", params.topicId);
      if (params.moduleId) qs.set("moduleId", params.moduleId);
      if (params.batchId) qs.set("batchId", params.batchId);
      if (params.page) qs.set("page", String(params.page));
      if (params.pageSize) qs.set("pageSize", String(params.pageSize));
      const q = qs.toString();
      return request<ReviewQueueResponse>(`/admin/review/queue${q ? `?${q}` : ""}`, {}, token);
    },

    reviewItemAction: (
      token: string,
      body: {
        type: "quiz" | "content" | "notes";
        id: string;
        questionId?: string;
        action: "approve" | "reject" | "retire" | "flag";
      }
    ) =>
      request<{ success: boolean; type?: string; id?: string; newStatus?: string; message?: string }>(
        "/admin/review/item/action",
        {
          method: "POST",
          body: JSON.stringify(body),
        },
        token
      ),

    editReviewQuestion: (
      token: string,
      body: {
        cacheRowId: string;
        questionId: string;
        question?: string;
        options?: string[];
        correct?: number;
        explanation?: string;
        figure_status?: "none" | "required" | "uploaded";
        figure_image?: string | null;
        figure_alt?: string | null;
        figure_location?: {
          source: string;
          pdfPage: number;
          region?: string;
          label?: string;
          description?: string;
          bbox?: number[];
        } | null;
      }
    ) =>
      request<{ success: boolean; message?: string }>(
        "/admin/review/item/edit",
        {
          method: "POST",
          body: JSON.stringify(body),
        },
        token
      ),

    uploadQuestionFigure: (token: string, file: File, alt: string) => {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("alt", alt);
      return request<{
        figure_image: string;
        figure_alt: string;
        figure_status: "uploaded";
      }>("/admin/questions/figure-upload", {
        method: "POST",
        body: fd,
      }, token);
    },

    deleteQuestionFigure: (token: string, url: string) =>
      request<{ ok: boolean; deleted: boolean }>(
        `/admin/questions/figure?url=${encodeURIComponent(url)}`,
        { method: "DELETE" },
        token
      ),

    bulkReviewAction: (
      token: string,
      body: {
        items: Array<{ type: string; id: string; questionId?: string }>;
        action: "approve" | "reject" | "retire" | "flag";
      }
    ) =>
      request<{ success: boolean; action: string; updatedCount: number; totalRequested: number; errors: string[] }>(
        "/admin/review/bulk/action",
        {
          method: "POST",
          body: JSON.stringify(body),
        },
        token
      ),

    approveBatchDrafts: (token: string, batchId: string) =>
      request<{ success: boolean; batchId: string; approvedRows: number; approvedQuestions: number; message?: string }>(
        `/admin/review/batches/${encodeURIComponent(batchId)}/approve`,
        { method: "POST" },
        token
      ),

    approveAllReviewItems: (
      token: string,
      params?: { kind?: "quiz" | "content" | "notes" | "all" },
    ) =>
      request<{
        success: boolean;
        totalApproved: number;
        approvedQuizzes: number;
        approvedContent: number;
        approvedNotes: number;
        message?: string;
      }>(
        "/admin/review/approve-all",
        {
          method: "POST",
          body: JSON.stringify(params ?? { kind: "all" }),
        },
        token,
      ),

    reviewStats: (
      token: string,
      params: { chapterId?: string; topicId?: string; moduleId?: string } = {}
    ) => {
      const qs = new URLSearchParams();
      if (params.chapterId) qs.set("chapterId", params.chapterId);
      if (params.topicId) qs.set("topicId", params.topicId);
      if (params.moduleId) qs.set("moduleId", params.moduleId);
      const q = qs.toString();
      return request<ReviewStatsResponse>(`/admin/review/stats${q ? `?${q}` : ""}`, {}, token);
    },

    reviewVersions: (
      token: string,
      params: { kind: "content" | "notes"; chapterId: string; topicId?: string }
    ) => {
      const qs = new URLSearchParams();
      qs.set("kind", params.kind);
      qs.set("chapterId", params.chapterId);
      if (params.topicId) qs.set("topicId", params.topicId);
      return request<ReviewQueueItem[]>(`/admin/review/versions?${qs.toString()}`, {}, token);
    },

    rollbackReviewVersion: (
      token: string,
      body: { kind: "content" | "notes"; id: string }
    ) =>
      request<{ success: boolean; id: string; version: number; status: string; message: string }>(
        "/admin/review/versions/rollback",
        {
          method: "POST",
          body: JSON.stringify(body),
        },
        token
      ),

    // Generation Jobs (TASK 6)
    createGenerationJob: (
      token: string,
      body: {
        resourceId: string;
        chapterId: string;
        topicId?: string | null;
        kind?: "all" | "notes" | "quiz";
        targetCount?: number;
        verifyAnswers?: boolean;
      }
    ) =>
      request<{ success: boolean; jobId: string; status: string; stage: string; scope: unknown }>(
        "/admin/generation/jobs",
        {
          method: "POST",
          body: JSON.stringify(body),
        },
        token
      ),

    listGenerationJobs: (
      token: string,
      params: { status?: string; chapterId?: string; page?: number; pageSize?: number } = {}
    ) => {
      const qs = new URLSearchParams();
      if (params.status && params.status !== "all") qs.set("status", params.status);
      if (params.chapterId) qs.set("chapterId", params.chapterId);
      if (params.page) qs.set("page", String(params.page));
      if (params.pageSize) qs.set("pageSize", String(params.pageSize));
      const q = qs.toString();
      return request<{ items: GenerationJobItem[]; total: number; page: number; pageSize: number }>(
        `/admin/generation/jobs${q ? `?${q}` : ""}`,
        {},
        token
      );
    },

    getGenerationJob: (token: string, jobId: string) =>
      request<GenerationJobItem>(`/admin/generation/jobs/${encodeURIComponent(jobId)}`, {}, token),

    socialLinks: {
      list: (token: string) =>
        request<SocialLink[]>("/admin/social-links", {}, token),
      create: (
        token: string,
        body: { platform: string; title: string; url: string; icon?: string; is_active?: boolean; sort_order?: number }
      ) =>
        request<SocialLink>(
          "/admin/social-links",
          { method: "POST", body: JSON.stringify(body) },
          token
        ),
      update: (
        token: string,
        id: string,
        body: Partial<{ platform: string; title: string; url: string; icon?: string; is_active?: boolean; sort_order?: number }>
      ) =>
        request<SocialLink>(
          `/admin/social-links/${encodeURIComponent(id)}`,
          { method: "PUT", body: JSON.stringify(body) },
          token
        ),
      delete: (token: string, id: string) =>
        request<{ ok: boolean; message: string }>(
          `/admin/social-links/${encodeURIComponent(id)}`,
          { method: "DELETE" },
          token
        ),
    },
    approveChapter: (token: string, chapterId: string) =>
      request<{ ok: boolean; chapterId: string; chapterTitle: string; isPublished: boolean; message: string }>(
        `/admin/chapters/${chapterId}/approve`,
        { method: "POST" },
        token
      ),

    // Courses & Pipeline (BATCH 4)
    listCourses: (token: string, search?: string) => {
      const qs = search ? `?search=${encodeURIComponent(search)}` : "";
      return request<{ items: AdminCourseListItem[]; total: number }>(`/admin/courses${qs}`, {}, token);
    },

    getCourseDetail: (token: string, courseId: string, includeArchived = false) =>
      request<AdminCourseDetailResponse>(
        `/admin/courses/${encodeURIComponent(courseId)}${includeArchived ? "?include_archived=true" : ""}`,
        {},
        token,
      ),

    toggleCourseItemVisibility: (
      token: string,
      courseId: string,
      itemType: string,
      itemId: string,
      isHidden?: boolean
    ) =>
      request<{ success: boolean; newStatus: string; isHidden: boolean }>(
        `/admin/courses/${encodeURIComponent(courseId)}/items/${encodeURIComponent(itemType)}/${encodeURIComponent(itemId)}/visibility`,
        { method: "POST", body: JSON.stringify({ is_hidden: isHidden }) },
        token
      ),

    toggleCourseItemAccess: (
      token: string,
      courseId: string,
      itemType: string,
      itemId: string,
      isFree?: boolean
    ) =>
      request<{ success: boolean; isFree: boolean; message: string }>(
        `/admin/courses/${encodeURIComponent(courseId)}/items/${encodeURIComponent(itemType)}/${encodeURIComponent(itemId)}/access`,
        { method: "POST", body: JSON.stringify({ is_free: isFree }) },
        token
      ),

    bulkSetCourseItemAccess: (
      token: string,
      courseId: string,
      items: { item_id: string; item_type: string }[],
      isFree: boolean
    ) =>
      request<{ ok: boolean; count: number; message: string }>(
        `/admin/courses/${encodeURIComponent(courseId)}/items/bulk-access`,
        { method: "POST", body: JSON.stringify({ items, is_free: isFree }) },
        token
      ),

    editCourseItem: (
      token: string,
      courseId: string,
      itemType: string,
      itemId: string,
      data: { title?: string; markdown?: string; difficulty?: string; is_free?: boolean; exam_year?: string }
    ) =>
      request<{ success: boolean; message: string }>(
        `/admin/courses/${encodeURIComponent(courseId)}/items/${encodeURIComponent(itemType)}/${encodeURIComponent(itemId)}`,
        { method: "PATCH", body: JSON.stringify(data) },
        token
      ),

    approveCourseItem: (
      token: string,
      courseId: string,
      itemType: string,
      itemId: string,
      force = false
    ) =>
      request<{ success: boolean; message: string }>(
        `/admin/courses/${encodeURIComponent(courseId)}/items/${encodeURIComponent(itemType)}/${encodeURIComponent(itemId)}/approve`,
        { method: "POST", body: JSON.stringify({ force }) },
        token
      ),

    deleteCourseItem: (
      token: string,
      courseId: string,
      itemType: string,
      itemId: string,
      draftOnly: boolean = false
    ) =>
      request<{ success: boolean; message: string }>(
        `/admin/courses/${encodeURIComponent(courseId)}/items/${encodeURIComponent(itemType)}/${encodeURIComponent(itemId)}${draftOnly ? "?draft_only=true" : ""}`,
        { method: "DELETE" },
        token
      ),

    addCourseResource: (token: string, courseId: string, formData: FormData) =>
      request<{ success: boolean; id: string; title: string; status: string; message?: string }>(
        `/admin/courses/${encodeURIComponent(courseId)}/resources/add`,
        { method: "POST", body: formData },
        token
      ),

    createCourseOrder: (
      token: string,
      courseId: string,
      moduleId: string,
      body: {
        profile?: string;
        questions_per_chapter?: number;
        force_reextract?: boolean;
        use_saved_extraction?: boolean;
        provider?: string;
      }
    ) =>
      request<any>(
        `/admin/courses/${encodeURIComponent(courseId)}/modules/${encodeURIComponent(moduleId)}/orders`,
        { method: "POST", body: JSON.stringify(body) },
        token
      ),

    aiProviders: {
      get: (token: string) => fetchAiProviders(request, token),
      update: (token: string, payload: { active_mode: string; providers: Record<string, any> }) =>
        updateAiProviders(request, token, payload),
      test: (token: string, provider: string) => testAiProvider(request, token, provider),
      // New table-based CRUD
      list: (token: string) => fetchAiProviderList(request, token),
      create: (token: string, payload: AiProviderCreatePayload) => createAiProvider(request, token, payload),
      patch: (token: string, id: string, payload: AiProviderUpdatePayload) =>
        updateAiProviderRow(request, token, id, payload),
      remove: (token: string, id: string) => deleteAiProvider(request, token, id),
      testById: (token: string, id: string) => testAiProviderById(request, token, id),
      reorder: (token: string, orderedIds: string[]) => reorderAiProviders(request, token, orderedIds),
    },

    savedExtraction: {
      getStatus: (token: string, courseId: string, moduleId: string) =>
        fetchSavedExtractionStatus(request, token, courseId, moduleId),
      retrySection: (token: string, courseId: string, moduleId: string, sectionId: string) =>
        retrySavedSection(request, token, courseId, moduleId, sectionId),
    },

    confirmCourseOrderChapters: (
      token: string,
      courseId: string,
      moduleId: string,
      orderId: string,
      chapters: Array<{ id?: string; idx: number; title: string; start_page: number; end_page: number; include: boolean }>
    ) =>
      request<any>(
        `/admin/courses/${encodeURIComponent(courseId)}/modules/${encodeURIComponent(moduleId)}/orders/${encodeURIComponent(orderId)}/confirm-chapters`,
        { method: "POST", body: JSON.stringify({ chapters }) },
        token
      ),

    cancelCourseOrder: (token: string, courseId: string, moduleId: string, orderId: string) =>
      request<any>(
        `/admin/courses/${encodeURIComponent(courseId)}/modules/${encodeURIComponent(moduleId)}/orders/${encodeURIComponent(orderId)}/cancel`,
        { method: "POST" },
        token
      ),

    resumeCourseOrder: (token: string, courseId: string, moduleId: string, orderId: string) =>
      request<any>(
        `/admin/courses/${encodeURIComponent(courseId)}/modules/${encodeURIComponent(moduleId)}/orders/${encodeURIComponent(orderId)}/resume`,
        { method: "POST" },
        token
      ),

    retryCourseOrderUnit: (token: string, courseId: string, moduleId: string, orderId: string, unitId: string) =>
      request<any>(
        `/admin/courses/${encodeURIComponent(courseId)}/modules/${encodeURIComponent(moduleId)}/orders/${encodeURIComponent(orderId)}/retry-unit/${encodeURIComponent(unitId)}`,
        { method: "POST" },
        token
      ),

    getManualExtractionTemplate: (
      token: string,
      courseId: string,
      params: {
        resourceId?: string;
        preference: "quiz" | "past_exam" | "notes" | "both" | "NOTES" | "QUIZ" | "BOTH" | "PAST_EXAM";
        chapterId?: string;
        examName?: string;
        questionsPerLevel?: number;
        universityId?: string;
        examYear?: string;
        examTerm?: string;
        fromQ?: number;
        toQ?: number;
      }
    ) => {
      const q = new URLSearchParams({
        preference: params.preference,
      });
      if (params.resourceId) q.set("resource_id", params.resourceId);
      if (params.chapterId) q.set("chapter_id", params.chapterId);
      if (params.examName) q.set("exam_name", params.examName);
      if (params.questionsPerLevel !== undefined) q.set("questions_per_level", String(params.questionsPerLevel));
      if (params.universityId) q.set("university_id", params.universityId);
      if (params.examYear) q.set("exam_year", params.examYear);
      if (params.examTerm) q.set("exam_term", params.examTerm);
      if (params.fromQ !== undefined) q.set("from_q", String(params.fromQ));
      if (params.toQ !== undefined) q.set("to_q", String(params.toQ));
      return request<{
        template: string;
        filename: string;
        preference: string;
        courseName: string;
        courseCode?: string;
        courseType?: string;
        profileUsed?: string;
        resourceTitle?: string | null;
        chapterTitle?: string | null;
        charCount: number;
        hasSourceText: boolean;
      }>(`/admin/courses/${encodeURIComponent(courseId)}/manual-extraction-template?${q.toString()}`, {}, token);
    },

    submitManualIngest: (token: string, courseId: string, formData: FormData) =>
      request<{
        success: boolean;
        type: string;
        id: string;
        title: string;
        count?: number;
        status: string;
        origin?: string;
        source?: string;
        message: string;
      }>(
        `/admin/courses/${encodeURIComponent(courseId)}/manual-ingest`,
        { method: "POST", body: formData },
        token
      ),

    // Sub-namespace alias for courses to support api.admin.courses.*
    courses: {
      getSemesterImpact: (token: string, courseId: string, newSemester?: number | null) =>
        api.admin.getCourseSemesterImpact(token, courseId, newSemester),

      update: (
        token: string,
        id: string,
        body: {
          name?: string;
          code?: string;
          description?: string | null;
          stream_id?: string | null;
          semester?: number | null;
          image_url?: string | null;
          is_premium?: boolean;
          course_type?: string | null;
          extraction_profile?: string | null;
        }
      ) =>
        request<CatalogCourse>(`/admin/courses/${id}`, { method: "PATCH", body: JSON.stringify(body) }, token),

      getManualExtractionTemplate: (
        token: string,
        courseId: string,
        params: {
          resourceId?: string;
          preference: "quiz" | "past_exam" | "notes" | "both" | "NOTES" | "QUIZ" | "BOTH" | "PAST_EXAM";
          chapterId?: string;
          examName?: string;
          questionsPerLevel?: number;
          universityId?: string;
          examYear?: string;
          examTerm?: string;
          fromQ?: number;
          toQ?: number;
        }
      ) => {
        const q = new URLSearchParams({
          preference: params.preference,
        });
        if (params.resourceId) q.set("resource_id", params.resourceId);
        if (params.chapterId) q.set("chapter_id", params.chapterId);
        if (params.examName) q.set("exam_name", params.examName);
        if (params.questionsPerLevel !== undefined) q.set("questions_per_level", String(params.questionsPerLevel));
        if (params.universityId) q.set("university_id", params.universityId);
        if (params.examYear) q.set("exam_year", params.examYear);
        if (params.examTerm) q.set("exam_term", params.examTerm);
        if (params.fromQ !== undefined) q.set("from_q", String(params.fromQ));
        if (params.toQ !== undefined) q.set("to_q", String(params.toQ));
        return request<{
          template: string;
          filename: string;
          preference: string;
          courseName: string;
          courseCode?: string;
          courseType?: string;
          profileUsed?: string;
          resourceTitle?: string | null;
          chapterTitle?: string | null;
          charCount: number;
          hasSourceText: boolean;
        }>(`/admin/courses/${encodeURIComponent(courseId)}/manual-extraction-template?${q.toString()}`, {}, token);
      },

      submitManualIngest: (token: string, courseId: string, formData: FormData) =>
        request<{
          success: boolean;
          type: string;
          id: string;
          title: string;
          count?: number;
          status: string;
          origin?: string;
          source?: string;
          message: string;
        }>(
          `/admin/courses/${encodeURIComponent(courseId)}/manual-ingest`,
          { method: "POST", body: formData },
          token
        ),
    },

    // Catalog namespace supporting api.admin.catalog.courses.*
    catalog: {
      courses: {
        getSemesterImpact: (token: string, courseId: string, newSemester?: number | null) =>
          api.admin.getCourseSemesterImpact(token, courseId, newSemester),
        update: (
          token: string,
          id: string,
          body: {
            name?: string;
            code?: string;
            description?: string | null;
            stream_id?: string | null;
            semester?: number | null;
            image_url?: string | null;
            is_premium?: boolean;
          }
        ) =>
          request<CatalogCourse>(`/admin/courses/${id}`, { method: "PATCH", body: JSON.stringify(body) }, token),
        list: (token: string, search?: string) => api.admin.catalogCourses(token, search),
        create: (
          token: string,
          body: { name: string; code: string; description?: string; stream_id?: string; semester?: number | null; image_url?: string | null }
        ) => api.admin.createCourse(token, body),
        delete: (token: string, id: string) => api.admin.deleteCourse(token, id),
      },
      universities: {
        list: (token: string, search?: string) => api.admin.catalogUniversities(token, search),
        create: (token: string, body: { name: string; short_name: string; region?: string }) =>
          api.admin.createUniversity(token, body),
        update: (token: string, id: string, body: any) => api.admin.updateUniversity(token, id, body),
        delete: (token: string, id: string) => api.admin.deleteUniversity(token, id),
      },
      streams: {
        list: (token: string, search?: string) => api.admin.catalogStreams(token, search),
        create: (token: string, body: { name: string; code?: string; description?: string; is_active?: boolean }) =>
          api.admin.createStream(token, body),
        update: (token: string, id: string, body: any) => api.admin.updateStream(token, id, body),
        delete: (token: string, id: string) => api.admin.deleteStream(token, id),
      },
    },
  },
};
